import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { confirmAlert } from "react-confirm-alert";
import {
  faPen,
  faPaperPlane,
  faPlus,
  faRefresh,
  faTrash,
  faEraser,
  faMagnifyingGlass,
  faChevronDown,
  faChevronUp,
  faExclamationCircle,
  faEye,
  faTableColumns,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import NotyfContext from "../contexts/NotyfContext.js";
import useLayout from "../hooks/useLayout.js";
import { domainConfig } from "../config.js";
import DropzoneComponent from "./Dropzone.js";
import WhatsAppRegistrationDefaultsCard from "./WhatsAppRegistrationDefaultsCard.js";
import { isRegistrationWhatsAppTemplate, WHATSAPP_REGISTRATION_USER_TYPES } from "../utils/whatsappRegistration.js";
import { formatWhatsAppTemplateVersion } from "../utils/whatsappTemplateVersion.js";
import {
  getWhatsAppTemplateBlockMessages,
  getWhatsAppTemplateSavedAt,
  isDeletedWhatsAppTemplate,
  uniqueWhatsAppTemplateMessages,
} from "../utils/whatsappTemplatePresentation.js";
import { ShowConfirm } from "./Alerts.js";
import {
  createSystemWhatsAppTemplate,
  deleteSystemWhatsAppMetaTemplate,
  getSystemWhatsAppTemplates,
  recoverSystemWhatsAppTemplateSubmission,
  submitSystemWhatsAppTemplate,
  updateSystemWhatsAppTemplate,
  putSystemWhatsAppRegistrationTemplates,
} from "../api/whatsappSystem.js";

const PURPOSE_OPTIONS = [
  { value: "rsvp", label: "RSVP" },
  { value: "event_update", label: "Atualização do evento" },
  { value: "marketing", label: "Marketing" },
];

const CATEGORY_OPTIONS = [
  { value: "UTILITY", label: "Utilidade" },
  { value: "MARKETING", label: "Marketing" },
];

const TEMPLATE_VARIABLES = [
  ["nome", "Nome"], ["evento", "Evento"], ["event_name", "Nome do evento"],
  ["email", "E-mail"], ["user_id", "ID do participante"],
  ["user_access_code", "Código de acesso"], ["rsvp_link", "Link RSVP"],
  ["qrcode_with_user_access_code_url", "Link direto — Login automático no Networking"],
  ["qrcode_download_url", "Link direto do Código QR"],
];
const BUILTIN_VARIABLE_NAMES = new Set(TEMPLATE_VARIABLES.map(([name]) => name));

const STATUS_LABELS = {
  draft: { label: "Rascunho", bg: "secondary" },
  submitted: { label: "Em análise", bg: "secondary" },
  approved: { label: "Aprovado", bg: "success" },
  rejected: { label: "Recusado", bg: "danger" },
  paused: { label: "Pausado", bg: "dark" },
};

const emptyForm = (template = null) => {
  const components = template?.components || {};
  const header = components.header || {};
  const category = String(template?.proposed_category || "UTILITY").toUpperCase();
  const staleRemoteIdentity = Boolean(template?.template_id)
    && (!template?.provider_template_id || isDeletedRemoteIdentity(template));
  const hasLink = getVariables([header.text, components.body?.text, ...(components.buttons || []).map((button) => button.url)]).includes("rsvp_link");
  const replyActions = (components.buttons || []).map((button) => button.action).filter(Boolean);
  const hasReplies = replyActions.length > 0 && replyActions.every((action) => ["rsvp_yes", "rsvp_no", "rsvp_answer"].includes(action));
  return {
    template_id: template?.template_id || null,
    catalog_id: staleRemoteIdentity ? null : template?.catalog_id || null,
    catalog_content_hash: template?.catalog_content_hash || template?.content_hash || null,
    active_version: template?.active_version ?? template?.current_version ?? template?.template_version ?? null,
    current_version: template?.current_version ?? template?.active_version ?? template?.template_version ?? null,
    account_id: template?.account_id || null,
    sender_id: template?.sender_id ? String(template.sender_id) : "",
    internal_title: template?.internal_title || "",
    target_user_type: template?.target_user_type == null ? "" : String(template.target_user_type),
    purpose: category === "MARKETING"
      ? "marketing"
      : ["rsvp", "event_update"].includes(template?.purpose) ? template.purpose : "event_update",
    language: template?.language || "pt_BR",
    proposed_category: category,
    header_mode: header.type === "image" ? "image" : header.text ? "text" : "none",
    header_text: header.text || "",
    header_image_source: header.source || "static",
    header_image_url: header.url || "",
    body_text: components.body?.text || "",
    sample_values: components.sample_values && typeof components.sample_values === "object" ? components.sample_values : {},
    variable_map: template?.variable_map && typeof template.variable_map === "object" ? template.variable_map : {},
    footer_text: components.footer?.text || "",
    rsvp_mode: (hasLink && hasReplies) || (replyActions.length > 0 && !hasReplies) ? "legacy" : hasReplies ? "whatsapp" : "page",
    buttons: Array.isArray(components.buttons)
      ? components.buttons.map((button) => ({
        type: button.type || "url",
        text: button.text || "",
        url: button.url || "",
        action: button.action || "",
      }))
      : [],
  };
};

const getVariables = (parts) => {
  const variables = new Set();
  parts.forEach((part) => {
    String(part || "").replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (_, rawName) => {
      const name = rawName.trim();
      variables.add(name);
      return _;
    });
  });
  return [...variables];
};

const getErrorMessage = (error, fallback) => {
  const feedback = error.response?.data?.provider_feedback;
  if (typeof feedback?.message === "string" && feedback.message.trim()) {
    const message = [feedback.title, feedback.message].filter((value) => typeof value === "string" && value.trim()).join(": ");
    return message.replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[character]));
  }
  const code = error.response?.data?.code;
  const messages = {
    WHATSAPP_TEMPLATE_INVALID: "Revise os campos e a estrutura do modelo antes de enviá-lo à Meta.",
    WHATSAPP_TEMPLATE_NOT_APPROVED: "Somente modelos aprovados pela Meta podem ser usados em campanhas.",
    WHATSAPP_TEMPLATE_PENDING_REVIEW: "O modelo está em análise na Meta e ainda não pode ser alterado.",
    WHATSAPP_TEMPLATE_SUBMIT_FAILED: "A Meta não aceitou o envio. Suas alterações foram mantidas; revise o modelo e tente novamente.",
    WHATSAPP_TEMPLATE_VARIABLE_INVALID: "O modelo usa uma variável que não é permitida.",
    WHATSAPP_TEMPLATE_SAMPLE_REQUIRED: "Informe um exemplo para cada variável personalizada antes de enviar à Meta.",
    WHATSAPP_TEMPLATE_SAMPLE_INVALID: "Revise os exemplos das variáveis do modelo.",
    WHATSAPP_TEMPLATE_IMAGE_HOST_INVALID: "Use uma imagem hospedada no servidor de imagens R1.",
    WHATSAPP_TEMPLATE_IMAGE_URL_INVALID: "Informe uma URL HTTPS válida para a imagem.",
    WHATSAPP_TEMPLATE_IMAGE_DOWNLOAD_FAILED: "Não foi possível carregar a imagem para a validação da Meta.",
    WHATSAPP_TEMPLATE_IMAGE_TYPE_INVALID: "Use uma imagem PNG ou JPEG.",
    WHATSAPP_TEMPLATE_IMAGE_SIZE_INVALID: "A imagem de exemplo deve ter até 5 MB.",
    WHATSAPP_TEMPLATE_IMAGE_UPLOAD_FAILED: "Não foi possível enviar a imagem de exemplo à Meta.",
    WHATSAPP_APP_ID_NOT_CONFIGURED: "Configure o ID do aplicativo Meta no servidor para enviar modelos com imagem.",
    WHATSAPP_TEMPLATE_FOOTER_VARIABLE_INVALID: "O rodapé não pode conter variáveis do WhatsApp.",
    WHATSAPP_TEMPLATE_BUTTON_VARIABLE_INVALID: "Em botões, somente {{rsvp_link}} pode ser dinâmica.",
    WHATSAPP_TEMPLATE_PROVIDER_NOT_FOUND: "A Meta não encontrou este modelo no número e idioma configurados. Confira a conta WhatsApp antes de tentar novamente.",
    WHATSAPP_TEMPLATE_SUBMISSION_STATE_INVALID: "O estado ou a versão do modelo mudou. Atualize a lista antes de enviá-lo novamente.",
    WHATSAPP_PROVIDER_NOT_CONFIGURED: "O envio de modelos à Meta está desativado no servidor.",
    WHATSAPP_TEMPLATE_MIGRATION_REQUIRED: "A atualização do banco de dados é necessária para excluir este modelo sem perder as versões salvas. Peça a um administrador R1 para aplicar a migração pendente.",
    WHATSAPP_REGISTRATION_MIGRATION_REQUIRED: "A atualização do banco para configurar modelos por tipo de participante precisa ser aplicada.",
    WHATSAPP_TEMPLATE_SUBMISSION_UNCONFIRMED: "A resposta da Meta não foi confirmada. Atualize os modelos para consultar o status antes de tentar novamente.",
    WHATSAPP_TEMPLATE_SUBMISSION_TOO_RECENT: "A tentativa ainda não completou 15 minutos. Atualize o catálogo depois desse prazo para verificar se a Meta recebeu o modelo.",
    WHATSAPP_TEMPLATE_PROVIDER_FOUND: "A Meta ainda mostra este modelo no catálogo. Atualize o status antes de tentar novamente.",
    WHATSAPP_TEMPLATE_DELETE_FAILED: "Não foi possível remover o modelo do painel. Tente novamente.",
    WHATSAPP_TEMPLATE_IN_USE: "Este modelo está em uso por campanhas e não pode ser excluído na Meta agora. Conclua ou cancele essas campanhas primeiro.",
    WHATSAPP_TEMPLATE_DELETE_IN_PROGRESS: "A exclusão deste modelo na Meta ainda está em andamento. Sincronize o catálogo antes de repetir a ação.",
    WHATSAPP_TEMPLATE_DELETE_UNCONFIRMED: "A Meta não confirmou a exclusão. Sincronize o catálogo para verificar o estado antes de tentar novamente.",
    WHATSAPP_TEMPLATE_ALREADY_CONFIGURED: "Este modelo da Meta já está configurado no painel. Atualize a lista para abrir a configuração existente.",
    WHATSAPP_TEMPLATE_UNAVAILABLE: "A Meta não disponibiliza este modelo para configuração ou envio. Sincronize o catálogo para confirmar o estado.",
    WHATSAPP_TEMPLATE_UNSUPPORTED: "Este formato de modelo aparece no catálogo, mas ainda não pode ser configurado para envio pelo R1.",
    WHATSAPP_TEMPLATE_REMOTE_CONTENT_CHANGED: "O modelo mudou na Meta. Suas alterações foram mantidas; carregue a versão atual antes de enviar novamente.",
    WHATSAPP_TEMPLATE_VERSION_STALE: "A versão ativa mudou enquanto você editava. Atualize os modelos e revise novamente antes de salvar.",
    WHATSAPP_TEMPLATE_IDENTITY_CONFLICT: "Esta identidade da Meta já está associada a outra versão. Atualize os modelos e tente novamente.",
    WHATSAPP_TEMPLATE_LANGUAGE_IMMUTABLE: "A Meta não permite alterar o idioma de um modelo existente.",
    WHATSAPP_TEMPLATE_CATEGORY_CHANGED: "A Meta não permite alterar a categoria de um modelo aprovado.",
    WHATSAPP_ACCOUNT_UNAVAILABLE: "A conta WhatsApp está desconectada. Reconecte a integração para gerenciar os modelos.",
    WHATSAPP_META_MANAGEMENT_PERMISSION_REQUIRED: "O token do remetente precisa da permissão de gerenciamento do WhatsApp para gerenciar os modelos.",
  };
  return messages[code] || error.response?.data?.message || fallback;
};

const formatStatus = (status) => STATUS_LABELS[status] || { label: status || "Indefinido", bg: "secondary" };
const formatVersionStatus = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (["draft", "approved", "submitted", "rejected", "paused"].includes(normalized)) return formatStatus(normalized);
  if (normalized === "deleted") return { label: "Excluída", bg: "dark" };
  return { label: status || "Indefinido", bg: "secondary" };
};

const withRsvpMode = (form, mode) => {
  const stripLink = (text) => String(text || "").replace(/\{\{\s*rsvp_link\s*\}\}/g, "");
  const buttons = form.buttons.filter((button) => !button.action && !getVariables([button.url]).includes("rsvp_link"));
  if (mode === "whatsapp") {
    const reply = (action, fallback) => ({ type: "quick_reply", action, url: "", text: form.buttons.find((button) => button.action === action)?.text || fallback });
    const existingReplies = form.buttons.filter((button) => button.type === "quick_reply").map((button) => ({ ...button, action: button.action || "rsvp_answer", url: "" }));
    const replies = existingReplies.length ? existingReplies : [reply("rsvp_yes", "Sim"), reply("rsvp_no", "Não")];
    return { ...form, rsvp_mode: mode, header_text: stripLink(form.header_text), body_text: stripLink(form.body_text), buttons: [...replies, ...buttons.filter((button) => button.type !== "quick_reply")].slice(0, 3) };
  }
  return { ...form, rsvp_mode: mode, buttons: [...buttons.slice(0, 2), { type: "url", text: form.buttons.find((button) => getVariables([button.url]).includes("rsvp_link"))?.text || "Confirmar presença", url: "{{rsvp_link}}", action: "" }] };
};

const isCurrentTemplateVersion = (template, version) => {
  const versionNumber = Number(version.template_version);
  return version.is_current === true
    || (Number.isSafeInteger(versionNumber)
      && versionNumber === Number(template.current_version ?? template.active_version ?? template.template_version));
};

const formatCreatedAt = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

const templateReasonLabel = (reason) => {
  const labels = {
    image_header_requires_local_media_configuration: "configure a mídia do cabeçalho",
    dynamic_button_url_requires_local_rsvp_configuration: "configure o fluxo RSVP deste botão dinâmico",
    named_parameter_name_not_supported: "um nome de variável não é suportado",
    positional_parameter_index_invalid: "um índice de variável é inválido",
    positional_parameter_indexes_must_be_contiguous: "os índices das variáveis precisam começar em 1 e seguir em sequência",
    footer_variables_are_not_supported: "variáveis no rodapé não são suportadas",
    button_text_invalid_for_local_sender: "o texto de um botão excede o limite aceito",
    button_url_missing: "falta o endereço de um botão",
    body_text_missing: "falta o texto da mensagem",
    body_text_exceeds_local_limit: "o texto da mensagem excede o limite aceito",
    header_text_exceeds_local_limit: "o cabeçalho excede o limite aceito",
    footer_text_exceeds_local_limit: "o rodapé excede o limite aceito",
    too_many_buttons_for_local_sender: "há mais botões do que o R1 suporta",
  };
  if (!reason) return "";
  if (labels[reason]) return labels[reason];
  if (reason.startsWith("unsupported_header_format_")) return `o formato de cabeçalho ${reason.slice("unsupported_header_format_".length)} não é suportado`;
  if (reason.startsWith("unsupported_button_type_")) return `o botão ${reason.slice("unsupported_button_type_".length)} não é suportado`;
  if (reason.startsWith("unsupported_component_")) return `o componente ${reason.slice("unsupported_component_".length)} não é suportado`;
  return String(reason).replace(/_/g, " ");
};

const templateReasonList = (value) => (
  (Array.isArray(value) ? value : String(value || "").split(","))
    .map((reason) => templateReasonLabel(String(reason).trim()))
    .filter(Boolean)
);

const getTemplateFeedback = (template) => {
  const response = typeof template?.provider_response === "string"
    ? (() => { try { return JSON.parse(template.provider_response); } catch (_) { return {}; } })()
    : template?.provider_response || {};
  if (response.identity_conflict) return "O nome desta versão na Meta já corresponde a outro conteúdo. Salve uma nova versão para solicitar uma identidade separada.";
  return [template?.meta_rejection_reason, template?.rejection_reason, response.rejection_reason]
    .map((reason) => String(reason || "").trim())
    .find((reason) => reason && reason.toLocaleUpperCase("en-US") !== "NONE") || "";
};

const isDeletedRemoteIdentity = (template) => (
  template?.deletion_state === "deleted"
  || template?.provider_status === "deleted"
  || template?.catalog_status === "deleted"
);

const TemplateBlockFeedback = ({ title, messages }) => (
  <div role="alert" className="d-flex align-items-start gap-2 bg-light border-start border-3 border-secondary rounded px-3 py-2 mt-3">
    <FontAwesomeIcon icon={faExclamationCircle} className="text-secondary mt-1 flex-shrink-0" />
    <div className="small">
      <div className="fw-semibold text-dark">{title}</div>
      {messages.length > 1
        ? <ul className="mb-0 ps-3 text-muted">{messages.map((message) => <li key={message}>{message}</li>)}</ul>
        : <div className="text-muted">{messages[0] || "O servidor não informou o motivo. Atualize o catálogo para consultar o estado atual."}</div>}
    </div>
  </div>
);

const isSecureTemplateUrl = (value) => {
  if (value === "{{rsvp_link}}") return true;
  try {
    return new URL(value).protocol === "https:";
  } catch (_) {
    return false;
  }
};

const TemplatePreview = ({ form }) => {
  const replacements = {
    nome: "Mariana", evento: "Seu evento", event_name: "Seu evento",
    email: "mariana@exemplo.com", user_id: "123", user_access_code: "ABC123",
    rsvp_link: "https://evento.exemplo/confirmacao",
    qrcode_with_user_access_code_url: "https://evento.exemplo/qrcode/123",
    qrcode_download_url: "https://evento.exemplo/qrcode/123.png",
  };
  const substitute = (value) => String(value || "").replace(/\{\{\s*([a-z][a-z0-9_]*)\s*\}\}/g, (_, name) => (
    String(form.sample_values?.[name] || "").trim()
      || replacements[name]
      || name.split("_").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ")
  ));
  const body = substitute(form.body_text);
  const buttons = form.buttons.filter((button) => button.text.trim());
  return (
    <div>
      <div className="mx-auto bg-dark rounded-4 p-2 shadow-sm" style={{ maxWidth: 320 }}>
        <div className="bg-light rounded-3 overflow-hidden">
          <div className="bg-success text-white px-3 py-2 small">
            <FontAwesomeIcon icon={faWhatsapp} className="me-1" /> WhatsApp
          </div>
          <div className="p-3" style={{ minHeight: 230, background: "#efeae2" }}>
            <div className="bg-white rounded-3 shadow-sm overflow-hidden text-start">
              {form.header_mode === "text" && form.header_text.trim() && <div className="px-3 pt-3 fw-semibold">{substitute(form.header_text)}</div>}
              {form.header_mode === "image" && (
                form.header_image_source === "static" && form.header_image_url
                  ? <img src={form.header_image_url} alt="Prévia do cabeçalho" className="w-100" style={{ maxHeight: 180, objectFit: "cover" }} />
                  : <div className="m-3 border rounded bg-light d-flex align-items-center justify-content-center text-muted small" style={{ height: 120 }}>Prévia do cabeçalho QR Code</div>
              )}
              <div className="px-3 py-3 small" style={{ whiteSpace: "pre-wrap" }}>{body || "A prévia aparecerá aqui."}</div>
              {form.footer_text.trim() && <div className="px-3 pb-2 small text-muted">{form.footer_text}</div>}
              {buttons.length > 0 && (
                <div className="border-top">
                  {buttons.map((button, index) => (
                    <div key={`${button.type}-${index}`} className={`py-2 text-center small text-primary${index > 0 ? " border-top" : ""}`}>
                      {button.type === "url" ? "↗ " : "↩ "}{button.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const WhatsAppRegistrationDefaultsModal = ({ show, onHide, templates = [], registrationDefaults = {}, onSave }) => {
  const notyf = React.useContext(NotyfContext);
  const [saving, setSaving] = useState(false);
  const selectionsTouchedRef = useRef(false);
  const getSavedSelections = useCallback(() => Object.fromEntries(WHATSAPP_REGISTRATION_USER_TYPES.map(({ type }) => {
    const userType = String(type);
    const reference = registrationDefaults[userType] || {};
    const selected = templates.find((item) => Number(item.template_id) === Number(reference.template_id)
      && Number(item.template_version || item.active_version) === Number(reference.template_version)
      && isRegistrationWhatsAppTemplate(item, userType));
    return [userType, selected ? `${selected.template_id}:${selected.template_version || selected.active_version}` : ""];
  })), [registrationDefaults, templates]);
  const [selections, setSelections] = useState(getSavedSelections);

  useEffect(() => {
    if (show && !selectionsTouchedRef.current) setSelections(getSavedSelections());
  }, [getSavedSelections, show]);

  const save = async () => {
    if (saving) return;
    const references = {};
    const missingTypes = [];
    for (const { type, label } of WHATSAPP_REGISTRATION_USER_TYPES) {
      const userType = String(type);
      const options = templates.filter((item) => isRegistrationWhatsAppTemplate(item, userType));
      const value = selections[userType] || "";
      const selected = options.find((item) => `${item.template_id}:${item.template_version || item.active_version}` === value);
      if (options.length && !selected) {
        missingTypes.push(label);
        continue;
      }
      references[userType] = selected ? {
        template_id: Number(selected.template_id),
        template_version: Number(selected.template_version || selected.active_version),
      } : null;
    }
    if (missingTypes.length) {
      notyf?.open({ type: "warning", message: `Selecione um modelo aprovado para: ${missingTypes.join(", ")}.`, ripple: true, dismissible: true });
      return;
    }

    setSaving(true);
    try {
      const saved = await onSave?.(references);
      if (saved) onHide();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={() => { if (!saving) onHide(); }} size="lg" centered>
      <Modal.Header closeButton={!saving}>
        <Modal.Title>Modelos padrão de WhatsApp</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          {WHATSAPP_REGISTRATION_USER_TYPES.map(({ type, label }) => {
            const userType = String(type);
            const options = templates.filter((item) => isRegistrationWhatsAppTemplate(item, userType));
            return <Form.Group as={Col} xs={12} key={userType}>
              <Form.Label>{label}</Form.Label>
              <Form.Select
                value={selections[userType] || ""}
                onChange={(event) => {
                  selectionsTouchedRef.current = true;
                  setSelections((current) => ({ ...current, [userType]: event.target.value }));
                }}
                disabled={saving || options.length === 0}
              >
                {!selections[userType] && <option value="">{options.length ? "Selecione um modelo aprovado" : "Nenhum modelo aprovado disponível"}</option>}
                {options.map((item) => <option key={`${item.template_id}:${item.template_version || item.active_version}`} value={`${item.template_id}:${item.template_version || item.active_version}`}>
                  {item.internal_title} · versão {item.template_version || item.active_version}
                </option>)}
              </Form.Select>
            </Form.Group>;
          })}
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={save} disabled={saving}>
          {saving ? <><Spinner animation="border" size="sm" className="me-2" />Salvando...</> : "Salvar todos os modelos"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const TemplateEditorModal = ({ show, onHide, template: initialTemplate, account, onSaved, onRebase,
  templates = [], registrationDefaultsMode = false, registrationDefaults = {}, registrationUserType: initialRegistrationUserType = "0",
  defaultModelValue: initialDefaultModelValue = "", onSaveRegistrationDefaults,
  testableTemplateIds = [], onRequestTemplateTest }) => {
  const notyf = React.useContext(NotyfContext);
  const initialTemplateForEditor = initialTemplate || (registrationDefaultsMode
    ? { purpose: "event_update", target_user_type: Number(initialRegistrationUserType) }
    : null);
  const initialEditorForm = emptyForm(initialTemplateForEditor);
  const [template, setTemplate] = useState(initialTemplateForEditor);
  const [form, setForm] = useState(initialEditorForm);
  const [initialForm, setInitialForm] = useState(initialEditorForm);
  const [registrationUserType, setRegistrationUserType] = useState(String(initialRegistrationUserType));
  const [registrationModelValues, setRegistrationModelValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [submissionUnconfirmed, setSubmissionUnconfirmed] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activePane, setActivePane] = useState("split");
  const [previewVersion, setPreviewVersion] = useState(0);
  const submissionConfirmationOpenRef = useRef(false);
  const savingRef = useRef(false);
  const registrationDraftsRef = useRef({});
  const registrationInitialFormsRef = useRef({});
  const registrationTemplatesRef = useRef({});
  const registrationModelValuesRef = useRef({});
  const registrationDefaultsRef = useRef(registrationDefaults || {});
  const bodyInputRef = useRef(null);
  const headerInputRef = useRef(null);
  const variableFieldRef = useRef("body_text");
  const hasRemoteTemplate = Boolean(template?.provider_template_id);
  const catalogConflict = Boolean(hasRemoteTemplate && template?.content_hash && form.catalog_content_hash !== template.content_hash);
  const accountId = template?.account_id || account?.account_id || null;
  const canInsertRsvpLink = form.purpose === "rsvp" && form.rsvp_mode !== "whatsapp";
  const rsvpLinkButtonIndex = form.buttons.findIndex((button) => button.type === "url" && getVariables([button.url]).includes("rsvp_link"));

  useEffect(() => {
    if (!show) return;
    const desktop = window.matchMedia("(min-width: 768px)");
    setActivePane(desktop.matches ? "split" : "editor");
    setShowAdvanced(false);
    setPreviewVersion(0);
    variableFieldRef.current = "body_text";
    const onResize = () => {
      if (!desktop.matches) setActivePane((current) => current === "split" ? "editor" : current);
    };
    desktop.addEventListener("change", onResize);
    return () => desktop.removeEventListener("change", onResize);
  }, [show]);

  useEffect(() => {
    if (!show || !registrationDefaultsMode) return;
    const values = {};
    const draftForms = {};
    const initialForms = {};
    const selectedTemplates = {};
    const startingType = String(initialRegistrationUserType);
    const findTemplate = (type, value) => {
      const [templateId, templateVersion] = String(value || "").split(":");
      return templateId && templateVersion ? templates.find((item) => Number(item.template_id) === Number(templateId)
        && Number(item.template_version || item.active_version) === Number(templateVersion)
        && isRegistrationWhatsAppTemplate(item, type)) || null : null;
    };
    const findReferencedTemplate = (value) => {
      const [templateId, templateVersion] = String(value || "").split(":");
      return templateId && templateVersion ? templates.find((item) => Number(item.template_id) === Number(templateId)
        && Number(item.template_version || item.active_version) === Number(templateVersion)) || null : null;
    };
    const getBlankTemplate = (type) => ({
      purpose: "event_update",
      target_user_type: Number(type),
      account_id: account?.account_id || null,
    });

    WHATSAPP_REGISTRATION_USER_TYPES.forEach(({ type }) => {
      const userType = String(type);
      const reference = registrationDefaults?.[userType] || {};
      const referenceValue = reference.template_id && reference.template_version
        ? `${reference.template_id}:${reference.template_version}` : "";
      const matchedTemplate = findTemplate(userType, referenceValue);
      const referencedTemplate = findReferencedTemplate(referenceValue);
      const selectedTemplate = userType === startingType && initialTemplate && isRegistrationWhatsAppTemplate(initialTemplate, userType)
        ? initialTemplate
        : matchedTemplate;
      const selectedValue = selectedTemplate
        ? `${selectedTemplate.template_id}:${selectedTemplate.template_version || selectedTemplate.active_version}`
        : userType === startingType && initialDefaultModelValue ? String(initialDefaultModelValue) : referenceValue;
      const editorTemplate = selectedTemplate || (userType === startingType ? initialTemplate : null) || referencedTemplate
        || (referenceValue ? { template_id: Number(reference.template_id), template_version: Number(reference.template_version), internal_title: "Modelo salvo" } : null)
        || getBlankTemplate(userType);
      const editorForm = emptyForm(editorTemplate);
      values[userType] = selectedValue;
      const key = `${userType}:${selectedValue}`;
      draftForms[key] = editorForm;
      initialForms[key] = editorForm;
      selectedTemplates[key] = editorTemplate;
    });

    const startingValue = values[startingType] || String(initialDefaultModelValue || "");
    const startingKey = `${startingType}:${startingValue}`;
    registrationDraftsRef.current = draftForms;
    registrationInitialFormsRef.current = initialForms;
    registrationTemplatesRef.current = selectedTemplates;
    registrationModelValuesRef.current = values;
    registrationDefaultsRef.current = registrationDefaults || {};
    setRegistrationUserType(startingType);
    setRegistrationModelValues(values);
    const startingTemplate = selectedTemplates[startingKey] || initialTemplate || getBlankTemplate(startingType);
    const startingForm = draftForms[startingKey] || emptyForm(startingTemplate);
    setTemplate(startingTemplate);
    setForm(startingForm);
    setInitialForm(initialForms[startingKey] || startingForm);
    setSubmissionUnconfirmed(Boolean(startingTemplate?.submission_pending));
    setShowAdvanced(false);
  }, [show, registrationDefaultsMode]);

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const handleHeaderImageUploadSuccess = (filename) => {
    const imageServer = String(domainConfig.imageServer || "").replace(/\/+$/, "");
    if (!imageServer) {
      notyf?.open({ type: "danger", message: "O servidor de imagens não está configurado.", ripple: true, dismissible: true });
      return;
    }
    update("header_image_url", `${imageServer}/r1/${encodeURIComponent(filename)}`);
  };
  const handleHeaderImageUploadError = (error) => {
    const message = typeof error === "string"
      ? error
      : error?.response?.data?.message || error?.message || "Não foi possível enviar a imagem.";
    notyf?.open({ type: "danger", message, ripple: true, dismissible: true });
  };
  const insertVariable = (name) => {
    if (!name || saving) return;
    const field = showAdvanced && variableFieldRef.current === "header_text" && form.header_mode === "text" ? "header_text" : "body_text";
    const input = field === "header_text" ? headerInputRef.current : bodyInputRef.current;
    const value = form[field];
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? start;
    const token = `{{${name}}}`;
    const nextValue = value.slice(0, start) + token + value.slice(end);
    if (nextValue.length > (field === "header_text" ? 60 : 1024)) {
      notyf?.open({ type: "warning", message: "A variável ultrapassa o limite de caracteres deste campo.", ripple: true, dismissible: true });
      return;
    }
    update(field, nextValue);
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(start + token.length, start + token.length);
    });
  };
  const updatePurpose = (purpose) => setForm((current) => {
    const next = { ...current, purpose, proposed_category: purpose === "marketing" ? "MARKETING" : "UTILITY" };
    if (purpose === "rsvp") return current.purpose === "rsvp" ? next : withRsvpMode(next, "whatsapp");
    return { ...next, header_text: next.header_text.replace(/\{\{\s*rsvp_link\s*\}\}/g, ""), body_text: next.body_text.replace(/\{\{\s*rsvp_link\s*\}\}/g, ""), buttons: next.buttons.filter((button) => !button.action && !getVariables([button.url]).includes("rsvp_link")) };
  });
  const updateButton = (index, field, value) => setForm((current) => ({
    ...current,
    buttons: current.buttons.map((button, buttonIndex) => (
      buttonIndex === index ? { ...button, [field]: value } : button
    )),
  }));
  const textVariables = getVariables([
    form.header_mode === "text" ? form.header_text : "",
    form.body_text,
  ]);
  const availablePurposeOptions = PURPOSE_OPTIONS;

  const reload = async (templateId = form.template_id, { loadMetaContent = false } = {}) => {
    const result = await onSaved();
    if (!result) return null;
    const metaTemplate = result.templates?.find((item) => (templateId
      ? Number(item.template_id) === Number(templateId)
      : form.catalog_id && Number(item.catalog_id) === Number(form.catalog_id)));
    const operation = result.saveOperations?.find((item) => Number(item.template_id) === Number(templateId));
    const pending = result.pendingSubmissions?.find((item) => Number(item.template_id) === Number(templateId));
    const current = loadMetaContent ? metaTemplate : operation?.template || metaTemplate;
    if (current) {
      setTemplate(current);
      onRebase(current);
      if (!loadMetaContent) setForm((previous) => ({
        ...previous,
        template_id: current.template_id || previous.template_id,
        catalog_id: current.provider_template_id && !isDeletedRemoteIdentity(current) ? current.catalog_id || null : null,
        active_version: current.active_version ?? current.current_version ?? current.template_version ?? previous.active_version,
        current_version: current.current_version ?? current.active_version ?? current.template_version ?? previous.current_version,
        catalog_content_hash: current.content_hash && current.provider_content_hash === current.content_hash ? current.content_hash : null,
      }));
    }
    else if (pending) {
      const pendingTemplate = { ...template, template_id: templateId, submission_pending: true, status: "submitted", submitted_at: pending.submitted_at };
      setTemplate(pendingTemplate);
      onRebase(pendingTemplate);
    }
    setSubmissionUnconfirmed(Boolean(pending || current?.submission_pending));
    if (loadMetaContent && metaTemplate) {
      const nextForm = emptyForm(metaTemplate);
      setTemplate(metaTemplate);
      setForm(nextForm);
      setInitialForm(nextForm);
    }
    return { current, pending };
  };

  const refreshEditor = async ({ loadMetaContent = false } = {}) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setRefreshing(true);
    try {
      const result = await reload(form.template_id, { loadMetaContent });
      if (!result || (loadMetaContent && !result.current)) {
        notyf?.open({ type: "danger", message: "Não foi possível carregar a versão atual da Meta. Suas alterações foram mantidas.", ripple: true, dismissible: true });
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
      setRefreshing(false);
    }
  };

  const confirmLoadMetaContent = () => confirmAlert({
    closeOnEscape: false,
    closeOnClickOutside: false,
    customUI: ({ onClose }) => (
      <ShowConfirm title="Carregar versão da Meta?" description="O conteúdo em edição será substituído pela versão atual da Meta." textConfirm="Carregar versão" action={() => refreshEditor({ loadMetaContent: true })} onClose={onClose} />
    ),
  });

  const refreshSavedTemplate = async (templateId) => {
    const result = await onSaved(registrationDefaultsMode ? { templateId, refreshOnly: true } : undefined);
    const current = result?.templates?.find((item) => Number(item.template_id) === Number(templateId));
    if (current) {
      setTemplate(current);
      onRebase(current);
    }
    return result;
  };

  const persist = async (payload) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    let templateId = Number(form.template_id) || null;
    let persisted = false;
    try {
      const expectedActiveVersion = template?.active_version ?? template?.current_version ?? template?.template_version;
      const savePayload = templateId && expectedActiveVersion != null
        ? {
          ...payload,
          expected_active_version: expectedActiveVersion,
        }
        : payload;
      const response = templateId
        ? await updateSystemWhatsAppTemplate({ templateId, payload: savePayload })
        : await createSystemWhatsAppTemplate(payload);
      persisted = true;
      templateId = Number(response.data?.template_id || templateId);
      if (!Number.isSafeInteger(templateId) || templateId <= 0) {
        throw new Error("Não foi possível identificar o modelo salvo para enviá-lo à Meta.");
      }
      const savedVersion = response.data?.active_version ?? expectedActiveVersion ?? form.current_version;
      const savedForm = {
        ...form,
        template_id: templateId,
        active_version: savedVersion,
        current_version: savedVersion,
        sample_values: payload.components.sample_values,
      };
      setForm(savedForm);
      setInitialForm(savedForm);
      const rebasedTemplate = { ...(template || {}) };
      const nextTemplate = {
        ...rebasedTemplate,
        template_id: templateId,
        active_version: savedVersion,
        current_version: savedVersion,
        template_version: response.data?.template_version ?? savedVersion,
        status: response.data?.status ?? template?.status,
      };
      setTemplate(nextTemplate);
      onRebase(nextTemplate);
      if (response.data?.requires_submission === false) {
        notyf?.open({
          type: "success",
          message: response.data?.unchanged ? "Nenhuma alteração para salvar."
            : "Configuração salva sem nova análise da Meta. Campanhas já enfileiradas continuam na versão que já estava salva.",
          ripple: true,
          dismissible: true,
        });
        await refreshSavedTemplate(templateId);
        if (!registrationDefaultsMode) onHide();
        return;
      }
      const submission = await submitSystemWhatsAppTemplate(templateId, savedVersion);
      const status = submission.data?.status;
      const message = status === "approved"
        ? "Modelo aprovado pela Meta."
        : status === "rejected"
          ? "A Meta recusou o modelo. Confira o status e revise uma nova versão."
          : "Modelo enviado para validação da Meta.";
      notyf?.open({ type: status === "rejected" ? "danger" : "success", message, ripple: true, dismissible: true });
      await refreshSavedTemplate(templateId);
      if (!registrationDefaultsMode) onHide();
    } catch (error) {
      const code = error.response?.data?.code;
      const refreshed = await reload(templateId);
      if (!refreshed && (code === "WHATSAPP_TEMPLATE_SUBMISSION_UNCONFIRMED" || (persisted && !error.response))) setSubmissionUnconfirmed(true);
      notyf?.open({ type: "danger", message: getErrorMessage(error, "Não foi possível salvar o modelo na Meta."), ripple: true, dismissible: true });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const buildTemplatePayload = (sourceForm, sourceTemplate) => {
    const sourceAccountId = sourceTemplate?.account_id || account?.account_id || null;
    if (!sourceAccountId || !sourceForm.internal_title.trim() || !sourceForm.body_text.trim()) {
      return { error: "A conta WhatsApp precisa estar conectada; defina também o título do modelo e o texto da mensagem." };
    }
    if (sourceTemplate?.is_supported === false) {
      return { error: sourceTemplate.unsupported_reason || "Este formato de modelo ainda não pode ser configurado para envio." };
    }
    const buttons = sourceForm.buttons
      .filter((button) => button.text.trim())
      .map((button) => ({
        type: button.type,
        text: button.text.trim(),
        url: button.type === "url" ? button.url.trim() : "",
        ...(button.type === "quick_reply" && button.action ? { action: button.action } : {}),
      }));
    if (buttons.some((button) => button.type === "url" && !button.url)) return { error: "Todo botão de URL precisa ter um endereço.", expandAdvanced: true };
    if (buttons.some((button) => button.type === "url" && !isSecureTemplateUrl(button.url))) return { error: "Use uma URL HTTPS válida ou {{rsvp_link}} em um botão.", expandAdvanced: true };
    const variables = getVariables([
      sourceForm.header_mode === "text" ? sourceForm.header_text : "",
      sourceForm.body_text,
      sourceForm.footer_text,
      ...buttons.map((button) => button.url),
    ]);
    const textVariables = getVariables([
      sourceForm.header_mode === "text" ? sourceForm.header_text : "",
      sourceForm.body_text,
    ]);
    const customVariablesMissingSamples = textVariables.filter((name) => (
      !BUILTIN_VARIABLE_NAMES.has(name) && !String(sourceForm.sample_values?.[name] || "").trim()
    ));
    if (customVariablesMissingSamples.length) return { error: `Informe um exemplo para cada variável personalizada: ${customVariablesMissingSamples.map((name) => `{{${name}}}`).join(", ")}.`, expandAdvanced: true };
    const invalidSamples = textVariables.filter((name) => String(sourceForm.sample_values?.[name] || "").length > 256);
    if (invalidSamples.length) return { error: "Cada exemplo de variável pode ter no máximo 256 caracteres.", expandAdvanced: true };
    const invalidVariables = variables.filter((name) => !/^[a-z][a-z0-9_]{0,31}$/.test(name)
      || ["qrcode", "qrcode_with_user_access_code", "logo"].includes(name));
    if (invalidVariables.length) return { error: `Use variáveis com aliases em minúsculas e snake_case: ${invalidVariables.map((name) => `{{${name}}}`).join(", ")}.`, expandAdvanced: true };
    if (getVariables([sourceForm.footer_text]).length) return { error: "O rodapé não pode conter variáveis do WhatsApp.", expandAdvanced: true };
    if (sourceForm.header_mode === "text" && getVariables([sourceForm.header_text]).length > 1) return { error: "O cabeçalho pode conter no máximo uma variável.", expandAdvanced: true };
    if (buttons.some((button) => button.type === "url" && /\{\{[^}]+\}\}/.test(button.url) && button.url !== "{{rsvp_link}}")) {
      return { error: "Em botões, use uma URL fixa ou somente {{rsvp_link}} em um modelo RSVP.", expandAdvanced: true };
    }
    const hasRsvpLink = getVariables([
      sourceForm.header_mode === "text" ? sourceForm.header_text : "",
      sourceForm.body_text,
      ...buttons.filter((button) => button.type === "url").map((button) => button.url),
    ]).includes("rsvp_link");
    const hasRsvpReplies = buttons.some((button) => button.type === "quick_reply" && ["rsvp_yes", "rsvp_no", "rsvp_answer"].includes(button.action));
    if (sourceForm.purpose === "rsvp" && sourceForm.rsvp_mode !== "legacy"
      && ((sourceForm.rsvp_mode === "whatsapp" && (!hasRsvpReplies || hasRsvpLink))
        || (sourceForm.rsvp_mode === "page" && (!hasRsvpLink || buttons.some((button) => button.action))))) {
      return { error: "Revise o modo RSVP: resposta no WhatsApp precisa de um botão de resposta; confirmação na página precisa do link RSVP." };
    }
    if (sourceForm.purpose === "rsvp" && !hasRsvpLink && !hasRsvpReplies) return { error: "Um modelo RSVP precisa incluir {{rsvp_link}} ou um botão de resposta associado ao RSVP.", expandAdvanced: true };
    if (sourceForm.purpose !== "rsvp" && variables.includes("rsvp_link")) return { error: "{{rsvp_link}} só pode ser usado em um modelo cuja finalidade seja RSVP." };
    let staticHeaderUrlIsSecure = false;
    try {
      staticHeaderUrlIsSecure = new URL(sourceForm.header_image_url.trim()).protocol === "https:";
    } catch (_) {
      staticHeaderUrlIsSecure = false;
    }
    if (sourceForm.header_mode === "image" && sourceForm.header_image_source === "static" && !staticHeaderUrlIsSecure) {
      return { error: "Informe uma URL HTTPS válida para a imagem do cabeçalho.", expandAdvanced: true };
    }
    const sampleValues = textVariables.reduce((result, name) => {
      const sample = String(sourceForm.sample_values?.[name] || "").trim();
      if (sample) result[name] = sample;
      return result;
    }, {});
    return { payload: {
      account_id: Number(sourceAccountId),
      ...(sourceForm.sender_id ? { sender_id: Number(sourceForm.sender_id) } : {}),
      ...(sourceForm.catalog_id ? { catalog_id: Number(sourceForm.catalog_id) } : {}),
      ...(sourceForm.catalog_content_hash ? { catalog_content_hash: sourceForm.catalog_content_hash } : {}),
      internal_title: sourceForm.internal_title.trim(),
      target_user_type: sourceForm.target_user_type === "" ? null : Number(sourceForm.target_user_type),
      purpose: sourceForm.purpose,
      language: sourceForm.language,
      proposed_category: sourceForm.proposed_category,
      variable_map: sourceForm.variable_map || {},
      components: {
        header: sourceForm.header_mode === "text" && sourceForm.header_text.trim()
          ? { type: "text", text: sourceForm.header_text.trim() }
          : sourceForm.header_mode === "image"
            ? { type: "image", source: sourceForm.header_image_source, ...(sourceForm.header_image_source === "static" ? { url: sourceForm.header_image_url.trim() } : {}) }
            : null,
        body: { text: sourceForm.body_text.trim() },
        sample_values: sampleValues,
        footer: sourceForm.footer_text.trim() ? { text: sourceForm.footer_text.trim() } : null,
        buttons,
      },
    } };
  };

  const registrationModelValue = (item) => `${item.template_id}:${item.template_version || item.active_version}`;
  const findRegistrationModel = (userType, value, sourceTemplates = templates) => {
    const [templateId, templateVersion] = String(value || "").split(":");
    return templateId && templateVersion ? sourceTemplates.find((item) => Number(item.template_id) === Number(templateId)
      && Number(item.template_version || item.active_version) === Number(templateVersion)
      && isRegistrationWhatsAppTemplate(item, userType)) || null : null;
  };
  const registrationDraftKey = (userType, value) => `${String(userType)}:${String(value || "")}`;
  const blankRegistrationTemplate = (userType) => ({ purpose: "event_update", target_user_type: Number(userType), account_id: account?.account_id || null });
  const cacheActiveRegistrationDraft = () => {
    if (!registrationDefaultsMode) return;
    const key = registrationDraftKey(registrationUserType, registrationModelValuesRef.current[registrationUserType]);
    registrationDraftsRef.current[key] = form;
    registrationInitialFormsRef.current[key] = initialForm;
    registrationTemplatesRef.current[key] = template;
  };
  const loadRegistrationSelection = (userType, modelValue) => {
    const nextType = String(userType);
    const nextValue = String(modelValue || "");
    const key = registrationDraftKey(nextType, nextValue);
    const selectedTemplate = registrationTemplatesRef.current[key] || findRegistrationModel(nextType, nextValue) || blankRegistrationTemplate(nextType);
    const selectedForm = registrationDraftsRef.current[key] || emptyForm(selectedTemplate);
    const selectedInitialForm = registrationInitialFormsRef.current[key] || emptyForm(selectedTemplate);
    setRegistrationUserType(nextType);
    setTemplate(selectedTemplate);
    setForm(selectedForm);
    setInitialForm(selectedInitialForm);
    setSubmissionUnconfirmed(Boolean(selectedTemplate?.submission_pending));
    setShowAdvanced(false);
  };
  const changeDefaultSelection = (selection) => {
    if (!registrationDefaultsMode) return;
    cacheActiveRegistrationDraft();
    if (selection.modelValue !== undefined) {
      const nextType = String(selection.userType ?? registrationUserType);
      const nextValue = String(selection.modelValue || "");
      const values = { ...registrationModelValuesRef.current, [nextType]: nextValue };
      registrationModelValuesRef.current = values;
      setRegistrationModelValues(values);
      loadRegistrationSelection(nextType, nextValue);
      return;
    }
    if (selection.userType !== undefined) {
      const nextType = String(selection.userType);
      const nextValue = registrationModelValuesRef.current[nextType] || "";
      loadRegistrationSelection(nextType, nextValue);
    }
  };

  const buildRegistrationReferences = (sourceTemplates = templates) => WHATSAPP_REGISTRATION_USER_TYPES.reduce((result, { type }) => {
    const userType = String(type);
    const value = registrationModelValuesRef.current[userType] || "";
    const selected = findRegistrationModel(userType, value, sourceTemplates);
    if (selected) {
      result[userType] = {
        template_id: Number(selected.template_id),
        template_version: Number(selected.template_version || selected.active_version),
      };
      return result;
    }
    const savedReference = registrationDefaultsRef.current?.[userType];
    result[userType] = savedReference?.template_id && savedReference?.template_version
      ? { template_id: Number(savedReference.template_id), template_version: Number(savedReference.template_version) }
      : null;
    return result;
  }, {});

  const runRegistrationBatch = async (drafts) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    const failures = [];
    const saved = [];
    try {
      for (const draft of drafts) {
        const { userType, userLabel, sourceForm, sourceTemplate, payload } = draft;
        try {
          const templateId = Number(sourceForm.template_id) || null;
          const expectedActiveVersion = sourceTemplate?.active_version ?? sourceTemplate?.current_version ?? sourceTemplate?.template_version;
          const savePayload = templateId && expectedActiveVersion != null ? { ...payload, expected_active_version: expectedActiveVersion } : payload;
          const response = templateId
            ? await updateSystemWhatsAppTemplate({ templateId, payload: savePayload })
            : await createSystemWhatsAppTemplate(payload);
          const savedTemplateId = Number(response.data?.template_id || templateId);
          if (!Number.isSafeInteger(savedTemplateId) || savedTemplateId <= 0) throw new Error("Não foi possível identificar o modelo salvo.");
          const savedVersion = response.data?.active_version ?? expectedActiveVersion ?? sourceForm.current_version;
          let submission = null;
          if (response.data?.requires_submission !== false) submission = await submitSystemWhatsAppTemplate(savedTemplateId, savedVersion);
          saved.push({
            userType,
            templateId: savedTemplateId,
            templateVersion: response.data?.template_version ?? savedVersion,
            form: { ...sourceForm, template_id: savedTemplateId, active_version: savedVersion, current_version: savedVersion, sample_values: payload.components.sample_values },
            template: { ...(sourceTemplate || {}), template_id: savedTemplateId, active_version: savedVersion, current_version: savedVersion, template_version: response.data?.template_version ?? savedVersion, status: submission?.data?.status ?? response.data?.status ?? sourceTemplate?.status },
          });
        } catch (error) {
          failures.push({ userLabel, message: getErrorMessage(error, "Não foi possível salvar o modelo.") });
        }
      }

      const refreshResult = await onSaved({ refreshOnly: true });
      const refreshedTemplates = refreshResult?.templates || templates;
      const values = { ...registrationModelValuesRef.current };
      saved.forEach((item) => {
        const refreshedTemplate = refreshedTemplates.find((candidate) => Number(candidate.template_id) === Number(item.templateId)) || item.template;
        const currentVersion = refreshedTemplate?.template_version || refreshedTemplate?.active_version || item.templateVersion;
        const value = `${item.templateId}:${currentVersion}`;
        values[item.userType] = value;
        const key = registrationDraftKey(item.userType, value);
        const formAfterSave = { ...item.form, active_version: currentVersion, current_version: currentVersion };
        registrationTemplatesRef.current[key] = refreshedTemplate;
        registrationDraftsRef.current[key] = formAfterSave;
        registrationInitialFormsRef.current[key] = formAfterSave;
        if (registrationUserType === item.userType) {
          setTemplate(refreshedTemplate);
          setForm(formAfterSave);
          setInitialForm(formAfterSave);
          setSubmissionUnconfirmed(Boolean(refreshedTemplate?.submission_pending));
        }
      });
      registrationModelValuesRef.current = values;
      setRegistrationModelValues(values);
      const references = buildRegistrationReferences(refreshedTemplates);
      const savedReferences = await onSaveRegistrationDefaults?.(references);
      if (savedReferences) registrationDefaultsRef.current = savedReferences;
      const savedStatuses = saved.map((item) => {
        const current = refreshedTemplates.find((candidate) => Number(candidate.template_id) === Number(item.templateId)) || item.template;
        return String(current?.provider_status || current?.status || "").toLowerCase();
      });
      const pending = savedStatuses.some((status) => ["submitted", "pending"].includes(status));
      const rejected = savedStatuses.some((status) => status === "rejected");
      if (failures.length) {
        notyf?.open({ type: "danger", message: `Falha ao salvar ${failures.map((item) => `${item.userLabel}: ${item.message}`).join("; ")}`, ripple: true, dismissible: true });
      }
      if (pending) {
        notyf?.open({ type: "warning", message: "Os modelos novos ou alterados foram salvos, mas só poderão ser definidos como padrão após aprovação da Meta. Os padrões anteriores válidos foram preservados.", ripple: true, dismissible: true });
      }
      if (rejected) {
        notyf?.open({ type: "danger", message: "A Meta recusou um dos modelos. O padrão anterior válido foi preservado; revise o modelo antes de tentar novamente.", ripple: true, dismissible: true });
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const saveRegistrationBatch = () => {
    if (savingRef.current || saving || submissionConfirmationOpenRef.current) return;
    cacheActiveRegistrationDraft();
    const drafts = [];
    for (const { type, label } of WHATSAPP_REGISTRATION_USER_TYPES) {
      const userType = String(type);
      const modelValue = registrationModelValuesRef.current[userType] || "";
      const key = registrationDraftKey(userType, modelValue);
      const sourceTemplate = registrationTemplatesRef.current[key] || findRegistrationModel(userType, modelValue) || blankRegistrationTemplate(userType);
      const sourceForm = registrationDraftsRef.current[key] || emptyForm(sourceTemplate);
      const originalForm = registrationInitialFormsRef.current[key] || emptyForm(sourceTemplate);
      if (sourceTemplate?.provider_template_id && sourceTemplate?.content_hash && sourceForm.catalog_content_hash !== sourceTemplate.content_hash) {
        loadRegistrationSelection(userType, modelValue);
        notyf?.open({ type: "warning", message: `${label}: o conteúdo do modelo mudou na Meta. Carregue a versão atual antes de salvar.`, ripple: true, dismissible: true });
        return;
      }
      if (JSON.stringify(sourceForm) === JSON.stringify(originalForm)) continue;
      if (sourceTemplate?.submission_pending || sourceTemplate?.status === "submitted") {
        loadRegistrationSelection(userType, modelValue);
        notyf?.open({ type: "warning", message: `${label}: aguarde a confirmação da Meta antes de alterar este modelo.`, ripple: true, dismissible: true });
        return;
      }
      const result = buildTemplatePayload(sourceForm, sourceTemplate);
      if (result.error) {
        loadRegistrationSelection(userType, modelValue);
        setShowAdvanced(Boolean(result.expandAdvanced));
        notyf?.open({ type: "warning", message: `${label}: ${result.error}`, ripple: true, dismissible: true });
        return;
      }
      drafts.push({ userType, userLabel: label, sourceForm, sourceTemplate, payload: result.payload });
    }

    if (!drafts.length) {
      runRegistrationBatch([]);
      return;
    }
    submissionConfirmationOpenRef.current = true;
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Salvar todos os modelos?"
          description="Os modelos novos ou alterados serão enviados à Meta para análise. Os padrões só serão atualizados para versões aprovadas; padrões anteriores válidos serão mantidos até a aprovação."
          textConfirm="Salvar todos os modelos"
          action={() => {
            submissionConfirmationOpenRef.current = false;
            runRegistrationBatch(drafts);
          }}
          onClose={() => {
            submissionConfirmationOpenRef.current = false;
            onClose();
          }}
        />
      ),
    });
  };

  const save = async () => {
    if (registrationDefaultsMode) {
      saveRegistrationBatch();
      return;
    }
    if (savingRef.current || saving || catalogConflict || submissionUnconfirmed || template?.submission_pending || template?.status === "submitted" || submissionConfirmationOpenRef.current) return;
    const result = buildTemplatePayload(form, template);
    if (result.error) {
      setShowAdvanced(Boolean(result.expandAdvanced));
      notyf?.open({ type: "warning", message: result.error, ripple: true, dismissible: true });
      return;
    }
    submissionConfirmationOpenRef.current = true;
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Enviar modelo para análise?"
          description="Uma alteração no conteúdo cria uma nova versão e exige análise da Meta. Ajustes de configuração que mantêm o mesmo conteúdo reutilizam a identidade aprovada. Campanhas já enfileiradas continuam na versão que já estava salva."
          textConfirm="Salvar alterações"
          action={() => persist(result.payload)}
          onClose={() => {
            submissionConfirmationOpenRef.current = false;
            onClose();
          }}
        />
      ),
    });
  };

  const hasUnsavedChanges = JSON.stringify(form) !== JSON.stringify(initialForm);
  const defaultModelValue = registrationModelValues[registrationUserType] || "";
  const registrationModelOptions = registrationDefaultsMode
    ? templates.filter((item) => isRegistrationWhatsAppTemplate(item, registrationUserType))
    : [];
  const currentModelIsSelectable = registrationModelOptions.some((item) => registrationModelValue(item) === defaultModelValue);
  const isTemplateTestable = testableTemplateIds.some((templateId) => String(templateId) === String(template?.template_id));
  const showEditor = activePane === "split" || activePane === "editor";
  const showPreview = activePane === "split" || activePane === "preview";
  const requestClose = () => {
    if (saving) return;
    onHide();
  };

  return (
    <>
    <Modal show={show} onHide={requestClose} fullscreen style={{ zIndex: 1062 }} backdropClassName="nested-modal-backdrop-lv2">
      <Modal.Header closeButton={!saving}>
          <Modal.Title>{registrationDefaultsMode ? "Modelos padrão de WhatsApp" : template?.template_id ? "Editar modelo" : "Novo modelo"}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0 d-flex flex-column" style={{ overflowY: "auto", backgroundColor: "#f8f9fa" }}>
        <div className="border-bottom py-3 flex-shrink-0">
        <div className="px-3">
        {catalogConflict && <Alert variant="primary" className="p-2">
          O modelo mudou na Meta. Suas alterações foram mantidas; carregue a versão atual antes de enviar novamente.
          <Button variant="primary" size="sm" className="ms-2" onClick={confirmLoadMetaContent} disabled={saving}>Carregar versão da Meta</Button>
        </Alert>}
        {!catalogConflict && template?.catalog_content_changed && <Alert variant="primary" className="p-2">
          A Meta atualizou o conteúdo deste modelo. A versão atual está exibida abaixo; revise os campos antes de salvar.
        </Alert>}
        {(submissionUnconfirmed || template?.submission_pending) && <Alert variant="primary" className="p-2">Aguardando a confirmação da Meta. Atualize o catálogo para conferir o envio antes de salvar novamente.</Alert>}
        </div>
        <Row className="mx-0">
          {registrationDefaultsMode ? <>
            <Form.Group as={Col} xs={12} className="mb-3">
              <Form.Label className="mb-1">Tipo de usuário</Form.Label>
              <Form.Select value={registrationUserType} onChange={(event) => changeDefaultSelection({ userType: event.target.value })} disabled={saving}>
                {WHATSAPP_REGISTRATION_USER_TYPES.map(({ type, label }) => <option key={type} value={String(type)}>{label}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6} className="mb-3 mb-md-0">
              <Form.Label className="mb-1">Título do modelo</Form.Label>
              <Form.Control value={form.internal_title} maxLength={100} onChange={(event) => update("internal_title", event.target.value)} disabled={saving} placeholder="Ex.: Convite RSVP - participantes" />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6} className="mb-3">
              <Form.Label className="mb-1">Modelo padrão</Form.Label>
              <Form.Select value={defaultModelValue} onChange={(event) => changeDefaultSelection({ userType: registrationUserType, modelValue: event.target.value })} disabled={saving}>
                <option value="">Criar novo modelo</option>
                {!currentModelIsSelectable && template?.template_id && defaultModelValue && <option value={defaultModelValue} disabled>
                  {template.internal_title || `Modelo #${template.template_id}`} · {template.status === "rejected" ? "recusado pela Meta" : template.status === "submitted" ? "aguardando aprovação" : "indisponível"}
                </option>}
                {registrationModelOptions.map((item) => <option key={item.template_id} value={registrationModelValue(item)}>
                  {item.internal_title} · versão {item.template_version || item.active_version}
                </option>)}
              </Form.Select>
              <Form.Text>O modelo será aplicado a este tipo após aprovação da Meta.</Form.Text>
            </Form.Group>
          </> : <Form.Group as={Col} xs={12} className="mb-3">
            <Form.Label className="mb-1">Tipo de participante</Form.Label>
            <Form.Select value={form.target_user_type} onChange={(event) => update("target_user_type", event.target.value)} disabled={saving}>
              <option value="">Todos os tipos</option>
              {WHATSAPP_REGISTRATION_USER_TYPES.map(({ type, label }) => <option key={type} value={type}>{label}</option>)}
            </Form.Select>
            <Form.Text>Define para qual tipo este modelo pode ser escolhido no envio após o cadastro.</Form.Text>
          </Form.Group>}
          {!registrationDefaultsMode && <Form.Group as={Col} xs={12} sm={6} className="mb-3 mb-sm-0">
            <Form.Label className="mb-1">Título do modelo</Form.Label>
            <Form.Control value={form.internal_title} maxLength={100} onChange={(event) => update("internal_title", event.target.value)} disabled={saving} placeholder="Ex.: Convite RSVP - participantes" />
          </Form.Group>}
          {!registrationDefaultsMode && <Form.Group as={Col} xs={12} sm={6}>
            <Form.Label className="mb-1">Finalidade</Form.Label>
            <Form.Select value={form.purpose} onChange={(event) => updatePurpose(event.target.value)} disabled={saving}>
              {availablePurposeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Form.Select>
          </Form.Group>}
          {form.purpose === "rsvp" && <Col xs={12} className="mt-3">
            <Form.Group>
              <Form.Label className="mb-1">Como o participante responde</Form.Label>
              <Form.Select value={form.rsvp_mode} onChange={(event) => setForm((current) => withRsvpMode(current, event.target.value))} disabled={saving}>
                {form.rsvp_mode === "legacy" && <option value="legacy">Configuração existente</option>}
                <option value="whatsapp">No WhatsApp, pelos botões de resposta</option>
                <option value="page">Na página de confirmação, pelo link RSVP</option>
              </Form.Select>
              {form.rsvp_mode !== "legacy" && <Form.Text className="text-muted d-block">{form.rsvp_mode === "whatsapp" ? "Os botões registram a resposta diretamente no WhatsApp. Associe cada botão a uma resposta na configuração do RSVP do evento." : "O participante abre a página de confirmação pelo botão ou pelo link na mensagem."}</Form.Text>}
              <Form.Text className="text-muted d-block">Escolha o RSVP do evento ao criar a campanha. Quantidade, textos e URLs dos botões são definidos neste modelo e enviados à Meta para aprovação. Os valores registrados são associados no RSVP.</Form.Text>
            </Form.Group>
            {form.rsvp_mode === "whatsapp" && <div className="mt-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>Botões de resposta</strong>
                <Button size="sm" variant="outline-primary" disabled={saving || form.buttons.length >= 3} onClick={() => setForm((current) => ({ ...current, buttons: [...current.buttons, { type: "quick_reply", text: "", url: "", action: "rsvp_answer" }] }))}><FontAwesomeIcon icon={faPlus} className="me-1" />Adicionar botão</Button>
              </div>
              {form.buttons.map((button, index) => button.action && <Row className="g-2 align-items-end mb-2" key={index}>
                <Form.Group as={Col}><Form.Label>Texto do botão {index + 1}</Form.Label><Form.Control value={button.text} maxLength={25} disabled={saving} onChange={(event) => updateButton(index, "text", event.target.value)} /></Form.Group>
                <Col xs="auto"><Button variant="outline-danger" disabled={saving || form.buttons.filter((item) => item.action).length === 1} onClick={() => setForm((current) => ({ ...current, buttons: current.buttons.filter((_, position) => position !== index) }))} aria-label={"Remover botão " + (index + 1)}>×</Button></Col>
              </Row>)}
              <Form.Text>O valor e a indicação de presença de cada resposta ficam no RSVP do evento. Alterar os textos ou a quantidade de botões deste modelo exige nova aprovação da Meta.</Form.Text>
            </div>}
            {form.rsvp_mode === "page" && <div className="mt-3">
              {rsvpLinkButtonIndex >= 0 ? <Form.Group>
                <Form.Label className="small mb-1">Texto do botão que abre a página de confirmação</Form.Label>
                <Form.Control value={form.buttons[rsvpLinkButtonIndex].text} maxLength={25} disabled={saving} onChange={(event) => updateButton(rsvpLinkButtonIndex, "text", event.target.value)} />
              </Form.Group> : <Button size="sm" variant="outline-primary" disabled={saving || form.buttons.length >= 3} onClick={() => setForm((current) => withRsvpMode(current, "page"))}>
                <FontAwesomeIcon icon={faPlus} className="me-1" />Adicionar botão com link RSVP
              </Button>}
              <Form.Text className="text-muted d-block">O endereço de confirmação é gerado para cada participante ao enviar a campanha.</Form.Text>
              {rsvpLinkButtonIndex < 0 && form.buttons.length >= 3 && <Form.Text className="text-muted d-block">Remova um botão nas opções avançadas para adicionar o botão RSVP.</Form.Text>}
            </div>}
          </Col>}
          <Col xs={12} className="pt-2">
            <Button variant="link" className="p-0 text-decoration-none fw-semibold d-flex align-items-center gap-2 text-muted" style={{ fontSize: 13, border: "none", color: "inherit" }} onClick={() => setShowAdvanced((current) => !current)} aria-controls="whatsapp-template-advanced" aria-expanded={showAdvanced}>
              Configurações avançadas de envio
              <FontAwesomeIcon icon={showAdvanced ? faChevronUp : faChevronDown} />
            </Button>
            <Collapse in={showAdvanced}>
              <div id="whatsapp-template-advanced">
                <div className="border p-3 mt-2">
                  <Row className="g-3">
                    <Form.Group as={Col} xs={12} md={4}>
                      <Form.Label className="mb-1">Conta proprietária dos modelos</Form.Label>
                      <Form.Control readOnly value={account?.display_name || template?.account_name || "R1 EvenTech · WhatsApp Business"} />
                      <Form.Text>WABA {template?.waba_id || account?.waba_id || "—"}</Form.Text>
                    </Form.Group>
                    <Form.Group as={Col} xs={12} sm={6} md={4}>
                      <Form.Label className="mb-1">Idioma</Form.Label>
                      <Form.Select value={form.language} onChange={(event) => update("language", event.target.value)} disabled={saving}>
                        <option value="pt_BR">Português (Brasil)</option>
                        <option value="en_US">English (US)</option>
                        <option value="es">Español</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group as={Col} xs={12} sm={6} md={4}>
                      <Form.Label className="mb-1">Categoria proposta</Form.Label>
                      <Form.Select value={form.proposed_category} onChange={(event) => setForm((current) => ({
                        ...current,
                        proposed_category: event.target.value,
                        purpose: event.target.value === "MARKETING" ? "marketing" : current.purpose === "marketing" ? "event_update" : current.purpose,
                      }))} disabled={saving}>
                        {CATEGORY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Row>
                  <Row className="g-3 mt-1">
                    <Form.Group as={Col} xs={12} md={4}>
                      <Form.Label className="mb-1">Cabeçalho</Form.Label>
                      <Form.Select value={form.header_mode} onChange={(event) => update("header_mode", event.target.value)} disabled={saving}>
                        <option value="none">Sem cabeçalho</option>
                        <option value="text">Texto</option>
                        <option value="image">Imagem</option>
                      </Form.Select>
                    </Form.Group>
                    {form.header_mode === "text" && <Form.Group as={Col} xs={12} md={8}>
                      <Form.Label className="mb-1">Texto do cabeçalho</Form.Label>
                      <Form.Control ref={headerInputRef} value={form.header_text} maxLength={60} onFocus={() => { variableFieldRef.current = "header_text"; }} onChange={(event) => update("header_text", event.target.value)} disabled={saving} />
                    </Form.Group>}
                    {form.header_mode === "image" && <>
                      <Form.Group as={Col} xs={12} sm={6} md={4}>
                        <Form.Label className="mb-1">Origem da imagem</Form.Label>
                        <Form.Select value={form.header_image_source} onChange={(event) => update("header_image_source", event.target.value)} disabled={saving}>
                          {form.header_image_source !== "static" && <option value={form.header_image_source}>Imagem QR da versão existente</option>}
                          <option value="static">Imagem por URL</option>
                        </Form.Select>
                      </Form.Group>
                      {form.header_image_source === "static" && <Form.Group as={Col} xs={12} sm={6} md={4}>
                        <Form.Label className="mb-1">URL da imagem</Form.Label>
                        <Form.Control type="url" value={form.header_image_url} onChange={(event) => update("header_image_url", event.target.value)} disabled={saving} placeholder="https://..." />
                        <Form.Text className="text-muted d-block">Use uma imagem PNG ou JPEG por HTTPS no servidor de imagens R1.</Form.Text>
                        <DropzoneComponent
                          className="mt-2"
                          type="image"
                          handlePreviewSuccess={handleHeaderImageUploadSuccess}
                          handlePreviewError={handleHeaderImageUploadError}
                          folder="media"
                          id="r1"
                          field="header_image_url"
                        />
                      </Form.Group>}
                    </>}
                    <Form.Group as={Col} xs={12}>
                      <Form.Label className="mb-1">Rodapé <span className="text-muted">(opcional)</span></Form.Label>
                      <Form.Control value={form.footer_text} maxLength={60} onChange={(event) => update("footer_text", event.target.value)} disabled={saving} />
                      <Form.Text>O rodapé não aceita variáveis.</Form.Text>
                    </Form.Group>
                  </Row>
                  <div className="mt-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <strong>Botões</strong>
                      <Button size="sm" variant="outline-primary" disabled={saving || form.buttons.length >= 3} onClick={() => setForm((current) => ({ ...current, buttons: [...current.buttons, { type: "url", text: "", url: "", action: "" }] }))}>
                        <FontAwesomeIcon icon={faPlus} className="me-1" />Adicionar
                      </Button>
                    </div>
                    {form.buttons.length === 0 && <small className="text-muted">Opcional. Use uma URL fixa ou {"{{rsvp_link}}"} em modelos RSVP.</small>}
                    {form.buttons.map((button, index) => (form.rsvp_mode === "whatsapp" && button.action ? null : (
                      <Row key={index} className="align-items-end mb-2 g-2">
                        <Form.Group as={Col} xs={12} md={2}>
                          <Form.Label className="small">Tipo</Form.Label>
                          <Form.Select size="sm" value={button.type} onChange={(event) => updateButton(index, "type", event.target.value)} disabled={saving || (form.rsvp_mode === "whatsapp" && Boolean(button.action))}>
                            <option value="url">URL</option>
                            <option value="quick_reply">Resposta rápida</option>
                          </Form.Select>
                        </Form.Group>
                        <Form.Group as={Col} xs={12} md={4}>
                          <Form.Label className="small">Texto</Form.Label>
                          <Form.Control size="sm" value={button.text} maxLength={25} onChange={(event) => updateButton(index, "text", event.target.value)} disabled={saving} />
                        </Form.Group>
                        {button.type === "quick_reply" && form.purpose === "rsvp" && form.rsvp_mode !== "page" && <Form.Group as={Col} xs={12} md={5}>
                          <Form.Label className="small">Comportamento</Form.Label>
                          <Form.Select size="sm" value={button.action} onChange={(event) => updateButton(index, "action", event.target.value)} disabled={saving}>
                            <option value="">Nenhuma</option>
                            <option value="rsvp_answer">Registrar resposta do RSVP</option>
                            {button.action === "rsvp_yes" && <option value="rsvp_yes">Resposta Sim existente</option>}
                            {button.action === "rsvp_no" && <option value="rsvp_no">Resposta Não existente</option>}
                          </Form.Select>
                        </Form.Group>}
                        {button.type === "url" && <Form.Group as={Col} xs={12} md={5}>
                          <Form.Label className="small">URL</Form.Label>
                          <Form.Control size="sm" type="url" value={button.url} onChange={(event) => updateButton(index, "url", event.target.value)} disabled={saving} placeholder="https://... ou {{rsvp_link}}" />
                        </Form.Group>}
                        <Col xs="auto" md={1}><Button size="sm" variant="outline-danger" disabled={saving || (form.rsvp_mode === "whatsapp" && Boolean(button.action))} onClick={() => setForm((current) => ({ ...current, buttons: current.buttons.filter((_, buttonIndex) => buttonIndex !== index) }))} aria-label={`Remover botão ${index + 1}`} title="Remover botão">×</Button></Col>
                      </Row>
                    )))}
                    {form.buttons.some((button) => button.type === "quick_reply") && <Form.Text className="text-muted d-block mt-2">Botões configurados para registrar uma resposta usam a associação definida no RSVP do evento. Outras respostas rápidas não alteram os registros do evento.</Form.Text>}
                  </div>
                  {textVariables.length > 0 && <div className="mt-3 pt-3 border-top">
                    <div className="fw-semibold mb-1">Exemplos para aprovação da Meta</div>
                    <Form.Text className="text-muted d-block mb-2">Informe exemplos para as variáveis usadas no cabeçalho e na mensagem. Você também pode escrever aliases personalizados, como {"{{nome_do_evento}}"}; nesse caso, o exemplo é obrigatório.</Form.Text>
                    <Row className="g-2">
                      {textVariables.map((name) => {
                        const isBuiltin = BUILTIN_VARIABLE_NAMES.has(name);
                        return <Form.Group as={Col} md={6} key={name} controlId={`whatsapp-template-sample-${name}`}>
                          <Form.Label className="small mb-1">{`{{${name}}}`}{isBuiltin && <span className="text-muted"> (opcional)</span>}</Form.Label>
                          <Form.Control size="sm" value={form.sample_values?.[name] || ""} maxLength={256} onChange={(event) => update("sample_values", { ...form.sample_values, [name]: event.target.value })} disabled={saving} placeholder={isBuiltin ? "Usar exemplo padrão" : "Exemplo obrigatório"} />
                        </Form.Group>;
                      })}
                    </Row>
                  </div>}
                </div>
              </div>
            </Collapse>
          </Col>
        </Row>
        </div>
        <div className="px-3 py-2 border-bottom d-flex align-items-center justify-content-between gap-2 flex-wrap flex-shrink-0">
          <div className="btn-group btn-group-sm" role="group" aria-label="Visualização do modelo">
            <button type="button" className={`btn ${activePane === "editor" ? "btn-primary border-dark border" : "btn-outline-secondary"}`} onClick={() => setActivePane("editor")} title="Somente editor" aria-label="Somente editor" aria-pressed={activePane === "editor"}><FontAwesomeIcon icon={faPen} /></button>
            <button type="button" className={`d-none d-md-block btn ${activePane === "split" ? "btn-primary border-dark border" : "btn-outline-secondary"}`} onClick={() => setActivePane("split")} title="Tela dividida" aria-label="Tela dividida" aria-pressed={activePane === "split"}><FontAwesomeIcon icon={faTableColumns} /></button>
            <button type="button" className={`btn ${activePane === "preview" ? "btn-primary border-dark border" : "btn-outline-secondary"}`} onClick={() => setActivePane("preview")} title="Somente prévia" aria-label="Somente prévia" aria-pressed={activePane === "preview"}><FontAwesomeIcon icon={faEye} /></button>
          </div>
          <div className="ms-auto d-flex align-items-center gap-2">
            {isTemplateTestable && <Button variant="primary" onClick={() => onRequestTemplateTest?.(template)} disabled={saving || hasUnsavedChanges} title={hasUnsavedChanges ? "Salve as alterações antes de testar o modelo aprovado." : "Disparar teste"} aria-label="Disparar teste">
              <FontAwesomeIcon icon={faPaperPlane} />
            </Button>}
            {(submissionUnconfirmed || template?.submission_pending) && <Button variant="primary" onClick={() => refreshEditor()} disabled={saving}>Atualizar</Button>}
            <Button variant="primary" onClick={save} disabled={saving || (!registrationDefaultsMode && (catalogConflict || submissionUnconfirmed || template?.submission_pending || template?.status === "submitted" || template?.is_supported === false))}>
              {saving ? <Spinner animation="border" size="sm" className="me-1" /> : <FontAwesomeIcon icon={faPaperPlane} className="me-1" />}
              {saving ? refreshing ? "Atualizando..." : "Salvando..." : registrationDefaultsMode ? "Salvar todos os modelos" : "Salvar modelo"}
            </Button>
          </div>
        </div>
        <div className="d-flex" style={{ flex: "1 0 500px", minHeight: 500, overflow: "hidden" }}>
          {showEditor && <div className={`d-flex flex-column${activePane === "split" ? " border-end" : ""}`} style={{ flex: activePane === "split" ? "0 0 50%" : 1, minWidth: 0, backgroundColor: "#fff" }}>
            <div className="d-flex flex-column flex-md-row flex-wrap px-3 py-2 align-items-start align-items-md-center border-bottom gap-2 flex-shrink-0" style={{ backgroundColor: "#f8f9fa", minHeight: 52 }}>
              <div className="me-auto"><FontAwesomeIcon icon={faPen} /><span className="ms-2">Editor</span></div>
              {canInsertRsvpLink && <Button size="sm" variant="outline-primary" disabled={saving} className="align-self-stretch align-self-md-auto" aria-label="Inserir link RSVP na mensagem" onClick={() => { variableFieldRef.current = "body_text"; insertVariable("rsvp_link"); }}>
                <FontAwesomeIcon icon={faPlus} className="me-1" />{"Inserir {{rsvp_link}}"}
              </Button>}
              <Form.Select value="" onChange={(event) => insertVariable(event.target.value)} disabled={saving} aria-label="Inserir variável no editor" className="w-auto align-self-stretch align-self-md-auto" style={{ fontSize: 13, minHeight: 32, minWidth: 200 }}>
                <option value="">Variáveis</option>
                {TEMPLATE_VARIABLES.filter(([name]) => name !== "rsvp_link" || canInsertRsvpLink).map(([name, label]) => <option key={name} value={name}>{label}</option>)}
              </Form.Select>
            </div>
            <div className="d-flex flex-column" style={{ flex: 1, minHeight: 0 }}>
              <Form.Control ref={bodyInputRef} as="textarea" value={form.body_text} maxLength={1024} onFocus={() => { variableFieldRef.current = "body_text"; }} onChange={(event) => update("body_text", event.target.value)} disabled={saving} aria-label="Mensagem do modelo" placeholder="Olá {{nome}}, temos uma atualização sobre {{evento}}." className="border-0 rounded-0 font-monospace" style={{ flex: 1, minHeight: 0, resize: "none", backgroundColor: "#1e1e1e", color: "#d4d4d4", fontSize: 13, lineHeight: 1.5, boxShadow: "none", padding: 16 }} />
              <div className="px-3 py-2 small text-muted border-top text-end" style={{ backgroundColor: "#f8f9fa" }}>{form.body_text.length}/1024 caracteres</div>
            </div>
          </div>}
          {showPreview && <div className="d-flex flex-column" style={{ flex: activePane === "split" ? "0 0 50%" : 1, minWidth: 0, backgroundColor: "#e8eaed" }}>
            <div className="d-flex px-3 py-2 align-items-center justify-content-between gap-2 border-bottom flex-shrink-0" style={{ backgroundColor: "#f8f9fa", minHeight: 52 }}>
              <div><FontAwesomeIcon icon={faEye} /><span className="ms-2">Prévia do WhatsApp</span></div>
              <Button variant="primary" size="sm" title="Atualizar prévia" aria-label="Atualizar prévia" onClick={() => setPreviewVersion((current) => current + 1)}><FontAwesomeIcon icon={faRefresh} /></Button>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "24px 16px" }}>
              <TemplatePreview key={previewVersion} form={form} />
            </div>
          </div>}
        </div>
      </Modal.Body>
    </Modal>
    </>
  );
};

const WhatsAppGlobalTemplatesCard = ({
  canLoad = true,
  onStatsChange,
  onTemplatesChange,
  onRegisterLoader,
  onRefresh,
  testableTemplateIds = [],
  onRequestTemplateTest,
}) => {
  const notyf = React.useContext(NotyfContext);
  const { setLoading: setGlobalLoading } = useLayout();
  const [templates, setTemplates] = useState([]);
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [saveOperations, setSaveOperations] = useState([]);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [actionKey, setActionKey] = useState("");
  const [showManager, setShowManager] = useState(false);
  const [editor, setEditor] = useState({ show: false, template: null });
  const [registrationDefaultsRefreshKey, setRegistrationDefaultsRefreshKey] = useState(0);
  const editorSessionRef = useRef(0);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const loadTemplates = useCallback(async ({ quiet = false, synchronize = false, force = false } = {}) => {
    if (!canLoad && !force) {
      setTemplates([]);
      setPendingSubmissions([]);
      setSaveOperations([]);
      setAccount(null);
      onTemplatesChange?.([]);
      setLoadError(false);
      setLoading(false);
      return;
    }
    if (!quiet) setLoading(true);
    setLoadError(false);
    try {
      const response = await getSystemWhatsAppTemplates({ synchronize });
      const nextTemplates = Array.isArray(response.data?.templates) ? response.data.templates : [];
      setTemplates(nextTemplates);
      const nextOperations = Array.isArray(response.data?.save_operations) ? response.data.save_operations : [];
      const nextPending = Array.isArray(response.data?.pending_submissions) ? response.data.pending_submissions : [];
      setPendingSubmissions(nextPending);
      setSaveOperations(nextOperations);
      setAccount(response.data?.account || null);
      onTemplatesChange?.(nextTemplates);
      return { templates: nextTemplates, account: response.data?.account || null, saveOperations: nextOperations, pendingSubmissions: nextPending };
    } catch (error) {
      setTemplates([]);
      setPendingSubmissions([]);
      setSaveOperations([]);
      setAccount(null);
      setLoadError(true);
      onTemplatesChange?.([]);
      if (!quiet) notyf?.open({ type: "danger", message: getErrorMessage(error, synchronize
        ? "Não foi possível sincronizar o catálogo de modelos da Meta."
        : "Não foi possível carregar os modelos globais de WhatsApp."), ripple: true, dismissible: true });
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [canLoad, notyf, onTemplatesChange]);

  const openEditor = async (template = null, operationId = null, targetUserType) => {
    setActionKey("open-editor");
    setGlobalLoading(true);
    try {
      const result = await loadTemplates({ quiet: true, synchronize: true });
      if (!result || result.account?.status !== "active") {
        notyf?.open({ type: !result ? "danger" : "warning", message: "Não foi possível consultar os modelos salvos na Meta. Atualize a conexão e tente novamente.", ripple: true, dismissible: true });
        return;
      }
      const current = operationId ? result.saveOperations.find((operation) => Number(operation.template_id) === Number(operationId))?.template : template ? result.templates.find((item) => (
        template.template_id
          ? Number(item.template_id) === Number(template.template_id)
          : String(item.provider_template_id) === String(template.provider_template_id)
            && Number(item.account_id) === Number(template.account_id)
      )) : null;
      if ((template || operationId) && !current) {
        notyf?.open({ type: "warning", message: operationId ? "Esta tentativa já foi resolvida. Consulte o modelo atualizado no catálogo." : "Este modelo não está mais disponível na Meta.", ripple: true, dismissible: true });
        return;
      }
      if (!template && !operationId) {
        notyf?.open({ type: "warning", message: "Templates precisam de aprovação da meta para serem utilizados", ripple: true, dismissible: true });
      }
      setEditor({
        show: true,
        template: current || (targetUserType !== undefined ? { purpose: "event_update", target_user_type: targetUserType } : null),
        sessionId: ++editorSessionRef.current,
        registrationDefaultsMode: false,
      });
    } finally {
      setActionKey("");
      setGlobalLoading(false);
    }
  };

  const openRegistrationDefaults = async (references = {}) => {
    setActionKey("open-editor");
    setGlobalLoading(true);
    try {
      const result = await loadTemplates({ quiet: true, synchronize: true });
      if (!result || result.account?.status !== "active") {
        notyf?.open({ type: !result ? "danger" : "warning", message: "Não foi possível consultar os modelos salvos na Meta. Atualize a conexão e tente novamente.", ripple: true, dismissible: true });
        return;
      }
      const firstType = String(WHATSAPP_REGISTRATION_USER_TYPES[0]?.type ?? "0");
      const reference = references[firstType] || {};
      const modelValue = reference.template_id && reference.template_version
        ? `${reference.template_id}:${reference.template_version}` : "";
      const current = result.templates.find((item) => Number(item.template_id) === Number(reference.template_id)
        && Number(item.template_version || item.active_version) === Number(reference.template_version));
      const initialTemplate = current || (modelValue
        ? { template_id: Number(reference.template_id), template_version: Number(reference.template_version), internal_title: "Modelo salvo" }
        : { purpose: "event_update", target_user_type: Number(firstType) });
      setEditor({
        show: true,
        template: initialTemplate,
        sessionId: ++editorSessionRef.current,
        registrationDefaultsMode: true,
        registrationDefaults: references,
        registrationUserType: firstType,
        defaultModelValue: modelValue,
      });
    } finally {
      setActionKey("");
      setGlobalLoading(false);
    }
  };

  const saveEditorTemplate = async () => loadTemplates({ quiet: true });

  const saveRegistrationDefaults = async (references) => {
    try {
      const { data } = await putSystemWhatsAppRegistrationTemplates(references);
      const savedReferences = data.templates || references;
      setRegistrationDefaultsRefreshKey((previous) => previous + 1);
      notyf?.open({ type: "success", message: "Modelos padrão atualizados.", ripple: true, dismissible: true });
      return savedReferences;
    } catch (error) {
      notyf?.open({ type: "danger", message: getErrorMessage(error, "Não foi possível salvar os modelos padrão. Confira se todos os modelos selecionados foram aprovados pela Meta."), ripple: true, dismissible: true });
      return null;
    }
  };

  const loadTemplatesRef = useRef(loadTemplates);
  loadTemplatesRef.current = loadTemplates;

  useEffect(() => {
    const load = ({ synchronize = false } = {}) => loadTemplatesRef.current({ force: true, synchronize });
    if (onRegisterLoader) return onRegisterLoader("whatsappTemplates", load);
    load();
  }, [onRegisterLoader]);

  const runAction = async (key, request, successMessage) => {
    setActionKey(key);
    try {
      await request();
      await loadTemplates({ quiet: true });
      if (successMessage) notyf?.open({ type: "success", message: successMessage, ripple: true, dismissible: true });
    } catch (error) {
      await loadTemplates({ quiet: true });
      notyf?.open({ type: "danger", message: getErrorMessage(error, "Não foi possível atualizar o modelo."), ripple: true, dismissible: true });
    } finally {
      setActionKey("");
    }
  };

  const confirmDelete = (template) => {
    const target = {
      templateId: template.template_id,
      catalogId: template.catalog_id,
      templateVersion: template.current_version ?? template.active_version ?? template.template_version,
    };
    if (!template.provider_template_id) {
      notyf?.open({ type: "warning", message: "Este item não tem uma referência Meta confirmada. Atualize o catálogo e tente novamente.", ripple: true, dismissible: true });
      return;
    }
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Excluir modelo?"
          description={`O modelo “${template.internal_title || template.name || template.provider_template_name || `#${template.provider_template_id}`}” será excluído da conta Meta ${template.waba_id || account?.waba_id || "R1"} e removido da lista do painel. Envios pendentes com este modelo serão cancelados. Envios em andamento ou sem confirmação precisam ser resolvidos antes da exclusão. O histórico será preservado.`}
          textConfirm="Excluir"
          action={() => runAction(`delete-${template.template_id || template.catalog_id}`, () => deleteSystemWhatsAppMetaTemplate(target), "Modelo removido da Meta e da lista do painel. O histórico foi preservado.")}
          onClose={onClose}
        />
      ),
    });
  };

  const confirmDeleteVersion = (template, version) => {
    const templateVersion = Number(version.template_version);
    const sharesCurrentIdentity = Boolean(version.provider_template_id && template.provider_template_id)
      && String(version.provider_template_id) === String(template.provider_template_id);
    if (!template.template_id || !Number.isSafeInteger(templateVersion) || templateVersion <= 0 || version.is_current === true || sharesCurrentIdentity || version.is_available === false || String(version.provider_status || "").toLowerCase() === "deleted") return;
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Excluir versão antiga da Meta?"
          description={`A versão ${formatWhatsAppTemplateVersion(templateVersion)}${version.provider_template_name ? ` (${version.provider_template_name})` : ""} será excluída da conta Meta. Envios pendentes com essa identidade Meta serão cancelados, inclusive os de outras versões locais que a compartilham. A versão atual do modelo e o histórico serão preservados.`}
          textConfirm="Excluir versão"
          action={() => runAction(
            `delete-${template.template_id}-v${templateVersion}`,
            () => deleteSystemWhatsAppMetaTemplate({ templateId: template.template_id, templateVersion }),
            `Versão ${formatWhatsAppTemplateVersion(templateVersion)} removida da Meta.`,
          )}
          onClose={onClose}
        />
      ),
    });
  };

  const confirmRecovery = (template) => {
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Verificar envio não confirmado?"
          description="O servidor consultará os modelos salvos na Meta para verificar se o envio foi recebido. Após 15 minutos, uma tentativa não aplicada pode ser liberada para um novo envio."
          textConfirm="Verificar catálogo"
          action={() => runAction(`recover-${template.template_id}`, () => recoverSystemWhatsAppTemplateSubmission(template.template_id), "Status do modelo atualizado a partir do catálogo da Meta.")}
          onClose={onClose}
        />
      ),
    });
  };

  const activeTemplates = useMemo(() => templates.filter((template) => !isDeletedWhatsAppTemplate(template)), [templates]);

  const counts = useMemo(() => activeTemplates.reduce((result, template) => {
    result.total += 1;
    if (template.status === "approved") result.approved += 1;
    if (template.status === "submitted") result.submitted += 1;
    return result;
  }, { total: 0, approved: 0, submitted: 0 }), [activeTemplates]);

  const visibleTemplates = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase("pt-BR");
    return activeTemplates.filter((template) => {
      const matchesStatus = statusFilter === "all" || template.status === statusFilter;
      const searchable = [
        template.internal_title,
        template.name,
        template.provider_template_name,
        template.components?.body?.text,
        template.purpose,
        template.account_name,
        template.waba_id,
      ].filter(Boolean).join(" ").toLocaleLowerCase("pt-BR");
      return matchesStatus && (!normalizedQuery || searchable.includes(normalizedQuery));
    });
  }, [activeTemplates, query, statusFilter]);

  useEffect(() => {
    onStatsChange?.(canLoad && !loadError ? counts : null);
  }, [canLoad, counts, loadError, onStatsChange]);

  return (
    <>
      <WhatsAppRegistrationDefaultsCard templates={templates} account={account}
        onEditDefaults={openRegistrationDefaults}
        refreshKey={registrationDefaultsRefreshKey}
        onRegisterLoader={onRegisterLoader} />
      <Card className="border shadow-sm mb-0" style={{ marginBottom: 0 }}>
        <Card.Body className="p-3">
          <div className="mb-2"><h6 className="fw-bold text-dark mb-0">Modelos de WhatsApp</h6></div>
          <p className="text-muted small mb-3">Catálogo de modelos pertencente à conta WhatsApp Business da R1. Os números remetentes dessa conta podem usar os modelos aprovados e configurados.</p>
          <div className="border-top pt-2 mt-2">
            {!canLoad ? (
              <div className="small text-muted">A configuração de WhatsApp está indisponível.</div>
            ) : loadError ? (
              <div className="small text-danger">Não foi possível carregar os modelos agora.</div>
            ) : (
              <div className="d-flex flex-column gap-1 small">
                <div className="d-flex justify-content-between text-muted"><span>Modelos no catálogo</span><strong className="text-dark">{counts.total}</strong></div>
                <div className="d-flex justify-content-between text-muted"><span>Aprovados para uso</span><strong className="text-dark">{activeTemplates.filter((template) => template.sendable === true && template.archived !== true).length}</strong></div>
              </div>
            )}
          </div>
          <div className="d-flex justify-content-end align-items-center mt-3 pt-2 border-top">
            <Button variant="primary" size="sm" className="py-1 px-3" onClick={() => setShowManager(true)} disabled={!canLoad}>
              <FontAwesomeIcon icon={faPen} className="me-1" />Editar
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Modal
        show={showManager}
        onHide={() => !actionKey && setShowManager(false)}
        fullscreen
        backdrop="static"
        keyboard={false}
        scrollable
      >
        <Modal.Header closeButton={!actionKey}>
          <Modal.Title>Modelos da conta WhatsApp R1</Modal.Title>
        </Modal.Header>
        <Button
          type="button"
          variant="primary"
          className="btn update-side-panel-button-modal"
          onClick={onRefresh}
          disabled={!canLoad || Boolean(actionKey) || loading}
          aria-label="Atualizar configurações do WhatsApp"
          title="Atualizar configurações do WhatsApp"
        >
          <FontAwesomeIcon icon={faRefresh} spin={loading} size="lg" />
        </Button>
        <Modal.Body className="p-3 p-md-4">
          <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
            <div className="text-muted">Modelos globais, suas versões salvas e o status de cada identidade na Meta.</div>
            <Button variant="primary" onClick={() => openEditor()} disabled={Boolean(actionKey) || account?.status !== "active"}>
              <FontAwesomeIcon icon={faPlus} className="me-1" />Novo modelo
            </Button>
          </div>
          {pendingSubmissions.map((pending) => <Alert key={`pending-${pending.template_id}`} variant="primary" className="p-2 mb-3">
            Aguardando a confirmação da Meta para o envio de {pending.provider_template_name || "um novo modelo"}. Esta versão fica indisponível para novas campanhas até a confirmação e aprovação.
            {pending.submitted_at && Date.now() - new Date(pending.submitted_at).getTime() >= 15 * 60 * 1000 && <Button size="sm" variant="primary" className="ms-2" disabled={Boolean(actionKey)} onClick={() => confirmRecovery(pending)}>Verificar envio não confirmado</Button>}
          </Alert>)}
          {saveOperations.map((operation) => <Alert key={`save-${operation.template_id}`} variant="primary" className="p-2 mb-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>O envio de <strong>{operation.internal_title || "um modelo"}</strong> não foi concluído. A tentativa foi preservada para correção e novo envio.</div>
              <Button variant="primary" disabled={Boolean(actionKey) || account?.status !== "active"} onClick={() => openEditor(null, operation.template_id)}>Retomar envio</Button>
            </div>
            {operation.feedback?.message && <div className="mt-2">{operation.feedback.title ? `${operation.feedback.title}: ` : ""}{operation.feedback.message}</div>}
          </Alert>)}
          {!canLoad ? <Alert variant="danger" className="p-2 mb-0">A configuração global do WhatsApp não está disponível. Atualize a configuração principal antes de gerenciar modelos.</Alert> : loadError ? (
            <Alert variant="danger" className="p-2 mb-0">Não foi possível carregar os modelos. Tente atualizar; nenhum dado de modelo foi alterado.</Alert>
          ) : !account && activeTemplates.length === 0 ? (
            <Alert variant="primary" className="p-2 mb-0">Conecte a conta WhatsApp Business da R1 para consultar e criar modelos na Meta.</Alert>
          ) : activeTemplates.length === 0 ? (
            <Alert variant="primary" className="p-2 mb-0">{account?.status === "active" ? "Nenhum modelo foi encontrado nesta conta. Crie um modelo para enviá-lo à Meta." : "Reconecte a integração para consultar os modelos salvos na Meta."}</Alert>
          ) : (
            <>
              <Row className="g-2 mb-3">
                <Col sm={6} md={6}>
                  <InputGroup>
                    <Form.Control value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar por nome, conteúdo ou WABA" aria-label="Buscar modelos" />
                    <InputGroup.Text className="text-muted"><FontAwesomeIcon icon={faMagnifyingGlass} /></InputGroup.Text>
                  </InputGroup>
                </Col>
                <Col xs={9} sm={5} md={5}><Form.Select className="form-control w-100" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} aria-label="Filtrar modelos por status"><option value="all">Todos os status</option>{Object.entries(STATUS_LABELS).map(([status, item]) => <option key={status} value={status}>{item.label}</option>)}</Form.Select></Col>
                <Col xs={3} sm={1} md={1}><Button variant="primary" className="w-100" onClick={() => { setQuery(""); setStatusFilter("all"); }} title="Limpar filtros" aria-label="Limpar filtros"><FontAwesomeIcon icon={faEraser} /></Button></Col>
              </Row>
              {visibleTemplates.length === 0 ? <h5 className="py-5 text-center opacity-50 mb-0">Nenhum modelo de WhatsApp encontrado</h5> : <div className="d-flex flex-column gap-3">
              {visibleTemplates.map((template) => {
                const status = formatStatus(template.status);
                const busy = Boolean(actionKey);
                const feedback = getTemplateFeedback(template);
                const targetId = template.template_id || `catalog-${template.catalog_id}`;
                const historyKey = String(template.template_id || targetId);
                const canDeleteTemplate = Boolean(template.provider_template_id) && !isDeletedRemoteIdentity(template) && !template.submission_pending;
                const canEditTemplate = template.is_supported !== false
                  && (template.template_id || template.is_available !== false)
                  && account?.status === "active";
                const savedAt = formatCreatedAt(getWhatsAppTemplateSavedAt(template));
                const sendBlockMessages = uniqueWhatsAppTemplateMessages([
                  ...(account?.status !== "active" ? ["Reconecte a conta WhatsApp Business da R1 para validar e salvar alterações."] : []),
                  ...(template.submission_pending ? ["Aguardando confirmação desta versão na Meta."] : []),
                  ...getWhatsAppTemplateBlockMessages(template),
                  ...(feedback ? [`Retorno da Meta: ${feedback}`] : []),
                  ...templateReasonList(template.unsupported_reason),
                  ...templateReasonList(template.configuration_reasons),
                ]);
                const historicalVersions = Array.isArray(template.versions)
                  ? template.versions.filter((version) => !isCurrentTemplateVersion(template, version) && !isDeletedWhatsAppTemplate(version))
                  : [];
                return (
                  <Card key={template.template_id || targetId} className="border mb-0">
                    <Card.Body className="p-3">
                      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                        <div className="me-auto">
                          <div className="fs-6 fw-bold">{template.internal_title || template.name || template.provider_template_name || `Modelo #${template.template_id || template.catalog_id}`}</div>
                          {(template.provider_category || template.proposed_category) && <div className="small text-muted pt-2">Categoria Meta: <strong>{template.provider_category || template.proposed_category}</strong></div>}
                        </div>
                        <div className="d-flex flex-column align-items-end gap-2 ms-auto">
                          <div className="small text-muted">Salvo em: {savedAt || "—"}</div>
                          <div className="d-flex align-items-center gap-2">
                            {template.submission_pending && template.submitted_at && Date.now() - new Date(template.submitted_at).getTime() >= 15 * 60 * 1000 && <Button size="sm" variant="primary" disabled={busy} onClick={() => confirmRecovery(template)} title="Verificar envio não confirmado" aria-label="Verificar envio não confirmado"><FontAwesomeIcon icon={faRefresh} /></Button>}
                            <Button size="sm" variant="primary" disabled={busy || !canDeleteTemplate} onClick={() => confirmDelete(template)} title="Excluir modelo" aria-label="Excluir modelo"><FontAwesomeIcon icon={faTrash} /></Button>
                            <Button size="sm" variant="primary" disabled={busy || !canEditTemplate} onClick={() => openEditor(template)} title={template.is_supported === false ? "Formato não suportado pelo R1" : account?.status !== "active" ? "Reconecte a conta WhatsApp para editar" : !template.template_id && template.is_available === false ? "Modelo indisponível na Meta" : "Editar modelo"}><FontAwesomeIcon icon={faPen} className="me-1" />Editar</Button>
                          </div>
                        </div>
                      </div>
                      {template.sendable === true && feedback && <Alert variant="primary" className="p-2 mt-3 mb-0"><strong>Retorno da Meta:</strong> {feedback}</Alert>}
                      <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                        <Badge bg={status.bg}>{status.label}</Badge>
                        {template.template_version != null && <Badge bg="primary" text="dark">{formatWhatsAppTemplateVersion(template.template_version)}</Badge>}
                        {template.purpose === "rsvp" && <Badge bg={template.sendable === true ? "success" : "light"} text={template.sendable === true ? undefined : "dark"}>{template.sendable === true ? "RSVP Ativo" : "RSVP"}</Badge>}
                      </div>
                      {template.sendable !== true && <TemplateBlockFeedback title="Modelo bloqueado para uso" messages={sendBlockMessages} />}
                      {template.sendable === true && account?.status !== "active" && <Alert variant="primary" className="p-2 mt-3 mb-0">Edição bloqueada: reconecte a conta WhatsApp Business da R1 para validar e salvar alterações.</Alert>}
                      <details className="small mt-3">
                        <summary className="text-muted">Detalhes técnicos da Meta</summary>
                        <div className="text-muted mt-1">Nome Meta: {template.provider_template_name || template.meta_template_name || "—"}</div>
                        <div className="text-muted">ID Meta: {template.provider_template_id || "—"}</div>
                      </details>
                      {historicalVersions.length > 0 && <details className="mt-3">
                        <summary className="small fw-semibold text-muted" style={{ cursor: "pointer" }}>Histórico de versões ({historicalVersions.length})</summary>
                        <div className="d-flex flex-column gap-3 mt-3">
                          {historicalVersions.map((version) => {
                            const sharesCurrentIdentity = Boolean(version.provider_template_id && template.provider_template_id)
                              && String(version.provider_template_id) === String(template.provider_template_id);
                            const versionStatus = formatVersionStatus(version.provider_status);
                            const canDeleteVersion = Boolean(template.template_id)
                              && !sharesCurrentIdentity
                              && Boolean(version.provider_template_id)
                              && String(version.provider_status || "").toLowerCase() !== "deleted"
                              && version.is_available !== false;
                            const versionDate = formatCreatedAt(getWhatsAppTemplateSavedAt(version));
                            const versionSendBlockMessages = getWhatsAppTemplateBlockMessages(version);
                            const deleteBlockedReason = sharesCurrentIdentity
                              ? "Esta versão compartilha a identidade Meta da versão atual."
                              : !version.provider_template_id || version.is_available === false || String(version.provider_status || "").toLowerCase() === "deleted"
                                ? "Esta versão não tem uma identidade disponível para excluir na Meta."
                                : "Esta versão não pode ser excluída agora.";
                            return <Card key={`${historyKey}-version-${version.template_version}`} className="border mb-0">
                              <Card.Body className="p-3">
                                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                                  <div className="me-auto">
                                    <div className="fs-6 fw-bold">{version.internal_title || template.internal_title || template.name || `Modelo #${template.template_id}`}</div>
                                    {version.proposed_category && <div className="small text-muted pt-2">Categoria Meta: <strong>{version.proposed_category}</strong></div>}
                                  </div>
                                  <div className="d-flex flex-column align-items-end gap-2 ms-auto">
                                    <div className="small text-muted">Salvo em: {versionDate || "—"}</div>
                                    <div className="d-flex align-items-center gap-2">
                                      <Button size="sm" variant="primary" disabled={busy || !canDeleteVersion} onClick={() => confirmDeleteVersion(template, version)} title={canDeleteVersion ? "Excluir versão da Meta" : deleteBlockedReason} aria-label={`Excluir versão ${formatWhatsAppTemplateVersion(version.template_version)} da Meta`}><FontAwesomeIcon icon={faTrash} /></Button>
                                    </div>
                                  </div>
                                </div>
                                <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                                  <Badge bg={versionStatus.bg}>{versionStatus.label}</Badge>
                                  <Badge bg="primary" text="dark">{formatWhatsAppTemplateVersion(version.template_version)}</Badge>
                                </div>
                                {version.sendable !== true && <TemplateBlockFeedback title="Versão bloqueada para uso" messages={versionSendBlockMessages} />}
                                <details className="small mt-3">
                                  <summary className="text-muted">Detalhes técnicos da Meta</summary>
                                  <div className="text-muted mt-1">Nome Meta: {version.provider_template_name || "—"}</div>
                                  <div className="text-muted">ID Meta: {version.provider_template_id || "—"}</div>
                                </details>
                              </Card.Body>
                            </Card>;
                          })}
                        </div>
                      </details>}
                    </Card.Body>
                  </Card>
                );
              })}
              </div>}
            </>
          )}
        </Modal.Body>
      </Modal>

      {editor.registrationDefaultsMode ? (
        <WhatsAppRegistrationDefaultsModal
          key={editor.sessionId || 0}
          show={editor.show}
          templates={templates}
          registrationDefaults={editor.registrationDefaults || {}}
          onSave={saveRegistrationDefaults}
          onHide={() => setEditor((current) => ({ ...current, show: false, template: null }))}
        />
      ) : (
        <TemplateEditorModal
          key={editor.sessionId || 0}
          show={editor.show}
          template={editor.template}
          account={account}
          templates={templates}
          testableTemplateIds={testableTemplateIds}
          onRequestTemplateTest={onRequestTemplateTest}
          onHide={() => setEditor((current) => ({ ...current, show: false, template: null }))}
          onSaved={saveEditorTemplate}
          onRebase={(template) => setEditor((current) => ({ ...current, template }))}
        />
      )}
    </>
  );
};

export default WhatsAppGlobalTemplatesCard;
